
  <template>
  <main class="tool">
    <div class="container">

      <!-- Hero -->
      <section class="card card--hero">
        <div class="hero">
          <div class="hero__text">
            <h1>Blockchain Analysis Tool</h1>
            <p class="sub">
              <!--Trace where funds went — exchanges, idle addresses, or risk endpoints.-->
              Research likely destinations, where funds went - exchanges, dormant wallets, etc.
            </p>
          </div>
          <div class="hero__meta">
            <p v-if="isLoggedIn" class="login ok">Logged in as <strong>{{ username }}</strong></p>
            <p v-else class="login warn">Please log in to access all features.</p>
          </div>
        </div>
      </section>
      <!-- Confirm Delete Modal -->
<teleport to="body">
  <div
    v-if="showDeleteConfirm"
    class="modal-overlay modal-overlay--floating"
    role="presentation"
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deleteConfirmTitle"
      tabindex="-1"
      ref="deleteConfirmModal"
      @keydown.esc.prevent="onDeleteCancel"
    >
      <div class="modal__head">
        <strong id="deleteConfirmTitle">Confirm delete</strong>
        <button @click="onDeleteCancel" class="close" aria-label="Close">×</button>
      </div>

      <div class="modal__body">
        <p>
          Are you sure you want to delete request
          <strong>{{ deleteRequestTarget?.request_id }}</strong>?
        </p>

        <p class="tiny muted" style="margin-top:.5rem;">
          This only removes it from your results history. If the request was already accepted into the queue,
          credits used are not refundable.
        </p>
      </div>

      <div class="modal__actions">
        <button class="btn btn--danger" @click="doDeleteConfirmed">
          Yes, delete
        </button>
        <button class="btn btn--ghost" @click="onDeleteCancel">
          Cancel
        </button>
      </div>
    </div>
  </div>
</teleport>

<!-- Confirm Bug Modal -->
<teleport to="body">
  <div
    v-if="showBugConfirm"
    class="modal-overlay modal-overlay--floating"
    role="presentation"
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bugConfirmTitle"
      tabindex="-1"
      ref="bugConfirmModal"
      @keydown.esc.prevent="showBugConfirm=false"
    >
      <div class="modal__head">
        <strong id="bugConfirmTitle">Confirm bug report</strong>
        <button @click="showBugConfirm=false" class="close" aria-label="Close">×</button>
      </div>

      <div class="modal__body">
        <p>Are you sure you want to report a bug for request
          <strong>{{ bugRequest?.request_id }}</strong>?</p>
      </div>

      <div class="modal__actions">
        <button class="btn btn--primary" @click="doReportBug">Yes, report</button>
        <button class="btn btn--ghost" @click="showBugConfirm=false">Cancel</button>
      </div>
    </div>
  </div>
</teleport>


      <!-- Access summary -->
      <section
        v-if="!isUnlimitedUser && (creditBalance !== null || isSubscriptionUser || subscriptionExpiresAt)"
        class="card card--access"
      >
        <div class="access">
          <div v-if="isSubscriptionUser">
            You’re on a subscription with
            <strong>{{ subscriptionDays }}</strong> day{{ subscriptionDays !== 1 ? 's' : '' }} remaining.
            <div class="muted small">Expiration: <strong>{{ formatDate(subscriptionExpiresAt) }}</strong></div>
          </div>

          <div v-else-if="subscriptionExpiresAt">
            ⏳ Your subscription expired on <strong>{{ formatDate(subscriptionExpiresAt) }}</strong>.
          </div>

          <div v-if="creditBalance !== null">
            Credits: <strong class="mono">{{ creditBalance }}</strong>
          </div>

    <router-link
  v-if="creditBalance === 0 && !isSubscriptionUser"
  class="btn linkish"
  to="/products"
  data-track="tool_get_access_click"
  :data-track-props="JSON.stringify({ source:'access_summary' })"
>
  Get more access
</router-link>
        </div>
      </section>

      <!-- Notifications -->
      <transition name="fade">
        <section v-if="notificationMessage" class="card card--notice" :class="notificationType">
          <div class="notice">
            <p>{{ notificationMessage }}</p>
            <!-- Dismiss notice -->
<button
  @click="clearNotification"
  class="btn btn--ghost"
  data-track="tool_notice_dismiss"
>
  Dismiss
</button>
          </div>
        </section>
      </transition>


      <teleport to="body">
  <div
    v-if="showReportModal"
    class="report-overlay"
    @click.self="closeReport"
  >
    <div
      class="modal modal--report"
      role="dialog"
      aria-modal="true"
      aria-label="Analysis report"
      ref="reportModal"
    >
      <div class="modal__head">
        <strong>Analysis report</strong>
        <button
  class="close"
  @click="closeReport"
  aria-label="Close"
  data-track="tool_modal_close_report"
>×</button>
      </div>

      <div class="modal__body">
        <!-- Context header -->
        <div class="report__meta">
          <div class="meta__row">
            <span class="label">Request ID</span>
            <span class="value mono">{{ reportRequest.request_id }}</span>
          </div>
          <div class="meta__row">
            <span class="label">Transaction</span>
            <span class="value mono truncate" :title="reportRequest.transaction_hash">
              {{ reportRequest.transaction_hash }}
            </span>
          </div>
          <div class="meta__row" v-if="reportRequest.to_address">
            <span class="label">To</span>
            <span class="value mono truncate" :title="reportRequest.to_address">
              {{ reportRequest.to_address }}
            </span>
          </div>
          <div class="meta__row">
            <span class="label">Mode</span>
            <span class="value">{{ getModeDisplay(reportRequest.mode) }}</span>
          </div>
          <div class="meta__row">
  <span class="label">Hop logic</span>
  <span class="value">{{ getHopLogicDisplay(reportRequest.hop_logic) }}</span>
</div>

          <div class="meta__row">
            <span class="label">Requested</span>
            <span class="value">{{ reportLocalTime(reportRequest.request_time) }}</span>
          </div>
          <div class="meta__row">
            <span class="label">Status</span>
            <span class="value">
              <span class="pill" :class="reportRequest.status === 'complete' ? 'pill--ok' : 'pill--warn'">
                {{ reportRequest.status }}
              </span>
            </span>
          </div>
        </div>

       <!-- Pretty report body -->
<div class="report__body">
  <div class="report__text">

    <!-- Normal Mode (existing behavior) -->
    <template v-if="!isAdvancedReport">
      <div
        v-for="(line, idx) in humanReport"
        :key="'n-'+idx"
        class="report-line"
      >
        <img v-if="line.icon" :src="line.icon" alt="" class="flag" />
        <span>{{ line.text }}</span>
      </div>

      <details class="raw-report">
        <summary>Raw report</summary>
        <pre>{{ reportRequest.result }}</pre>
      </details>
    </template>

    <!-- Advanced Mode (new UX) -->
    <template v-else>
      <div
        v-for="(line, idx) in humanReport"
        :key="'a-'+idx"
        class="report-line"
      >
        <img v-if="line.icon" :src="line.icon" alt="" class="flag" />
        <span>{{ line.text }}</span>
      </div>

     <div class="advanced-actions">
  <button
    class="btn btn--primary btn--sm"
    @click="ensureAdvancedSheetLoaded"
    data-track="advanced_open_spreadsheet"
  >
    Open spreadsheet preview
  </button>

  <button class="btn btn--outline btn--sm" @click="copyRawTSV" data-track="advanced_copy_tsv">
    Copy TSV
  </button>
<!--
  <button class="btn btn--outline btn--sm" @click="downloadAdvanced('tsv')" data-track="advanced_download_tsv">
    Download .tsv
  </button>
-->
  <button class="btn btn--outline btn--sm" @click="downloadAdvanced('csv')" data-track="advanced_download_csv">
    Download .csv
  </button>
</div>

     <div
  v-if="showAdvancedSheet"
  class="spreadsheet-wrapper"
  style="margin-block:12px;"
>
  <SpreadsheetComponent
    ref="advancedSheet"
    :key="`adv-${reportRequest?.request_id || 'tmp'}`"
    :width="'100%'"
    :height="'420px'"
  />
</div>

      <details class="raw-report">
        <summary>Raw data (TSV)</summary>
        <pre>{{ reportRequest.result }}</pre>
      </details>
    </template>
  </div>
</div>

<section class="report__callout">
  <div class="callout__text">
    <strong>Need a certified, human-reviewed report?</strong>
    <p class="small muted" style="margin-top:.25rem;">
      A professional review can validate the trace, interpret edge cases
      (smart contracts, bridges, memos, new services), and produce a polished report document.
    </p>
  </div>
<button
  class="btn btn--cta btn--sm"
  @click="goToInvestigationFromReport"
  data-track="tool_modal_request_investigation"
  :data-track-props="JSON.stringify({ id: reportRequest.request_id })"
  title="Request a professional investigation"
>
  Request Investigation
</button>

</section>

        <!-- Actions -->
        <div class="report__actions">
        <button
  class="btn btn--image btn--sm"
  @click="openScreenshotAndClose(reportRequest.request_id)"
  data-track="tool_modal_open_screenshot"
  :data-track-props="JSON.stringify({ id: reportRequest.request_id })"
>
  Open screenshot
</button>
        <button
  class="btn btn--ghost btn--sm copy-btn"
  :class="{ 'is-copied': copiedReport }"
  @click="copyReport"
  :disabled="copyingReport"
  data-track="tool_modal_copy_report"
  aria-live="polite"
  aria-atomic="true"
>
  <span class="copy-label-default">Copy text</span>
  <span class="copy-label-done">Copied</span>
</button>


<!-- DISCLAIMER (new) -->
<section class="report__disclaimer" :aria-expanded="disclaimerOpen ? 'true' : 'false'">
<details class="disclaimer" :open="disclaimerOpen" @toggle="disclaimerOpen = $event.target.open">
<summary class="disclaimer__summary">
<strong>Disclaimer</strong>
<span class="disclaimer__hint" aria-hidden="true">(click to {{ disclaimerOpen ? 'collapse' : 'expand' }})</span>
</summary>


<div class="disclaimer__card" role="group" aria-label="Report disclaimer text">
<p class="muted"><em>PathTracer — Important Notice & Limitations</em></p>


<ol class="disclaimer__list">
<li><strong>Open‑source & third‑party data.</strong> Reports are generated from publicly available blockchains, block explorers, and other third‑party/open‑source sources. These sources can be incomplete, inaccurate, or change without notice. PathTracer does not control these sources and cannot guarantee their content.</li>
<li><strong>Point‑in‑time snapshot.</strong> Each report reflects data available at the time it was produced. Subsequent blocks, chain reorganizations, amended labels, or API corrections may alter interpretations.</li>
<li><strong>No guarantee of completeness, accuracy, or timeliness.</strong> The report is provided on an <q>as is</q> and <q>as available</q> basis without warranties of any kind, express or implied.</li>
<li><strong>User responsibility.</strong> You are solely responsible for independently verifying the results, obtaining professional advice where appropriate, and determining how to use or interpret the report.</li>
<li><strong>No legal, financial, or accounting advice.</strong> PathTracer reports are technical analyses and are not a substitute for legal counsel, financial due diligence, or accounting review.</li>
<li><strong>Assumption of risk; limitation of liability.</strong> To the maximum extent permitted by law, PathTracer, its operators, and affiliates are not liable for any decisions, actions, losses, or damages (direct, indirect, incidental, consequential, or otherwise) arising from or related to use of a report.</li>
<li><strong>Jurisdiction & compliance.</strong> You represent that your use complies with all applicable laws and you have any necessary authorizations to analyze, process, or store the data involved.</li>
<li><strong>Use of the term “Report.”</strong> “Report” includes the narrative text, flags, icons, screenshots, diagrams, raw output, and any derived visualizations packaged with this modal or export.</li>
<li><strong>Updates.</strong> PathTracer may update its methods, datasets, and this disclaimer at any time. </li>
<li><strong>Contact.</strong> For questions about methodology or to report suspected data errors, contact the PathTracer team.</li>
</ol>


<p class="muted">© {{ new Date().getFullYear() }} PathTracer. All rights reserved.</p>
</div>
</details>



</section>
        </div>
      </div>
    </div>
  </div>
</teleport>



      <!-- Loading gate -->
      <section v-if="loading" class="card center">
        <div class="spinner lg"></div>
        <p class="muted">Loading your account info…</p>
      </section>

      <section v-else class="grid">
        <!-- Request form -->
        <article class="card">
          <h2 class="card__title">New analysis</h2>
<p v-if="prefillMsg" class="notif notif--info" style="margin-bottom:8px">
  {{ prefillMsg }}
</p>

       <form @submit.prevent="handleSubmitRequest" class="form">

          <div class="field field--primary">
  <label for="transaction-hash">Transaction Hash</label>
  <input
  id="transaction-hash"
  v-model="transactionHash"
  :disabled="isSubmittingRequest"
  type="text"
  data-1p-ignore
  placeholder="0xdf158166d… (ETH) or 71a6c9… (BTC)"
  maxlength="150"                
  pattern="[A-Za-z0-9]{1,66}"    
  aria-label="Transaction Hash"
  autofocus
/>
  <p class="hint">
    Paste the <em>transaction hash</em> (receipt ID).
  </p>
</div>


            <div class="field">
              <label for="to-address">To Address <span class="muted">(optional)</span></label>
            <input
  id="to-address"
  v-model="toAddress"
  :disabled="isSubmittingRequest"
  type="text"
  placeholder="Optional: 0x… or BTC address"
  maxlength="100"
  data-1p-ignore
  pattern="[A-Za-z0-9]{1,64}"
  aria-label="To Address"
/>
            </div>

            <p v-if="validationError" class="error">{{ validationError }}</p>
<div class="field modes-row">
  <div class="modes-row__label">Modes:</div>

  <div class="modes-row__controls">
    <!-- Hop logic control -->
    <div class="modes-row__control">
      <label class="sr-only" for="hop-logic">Hop logic</label>

      <select
        id="hop-logic"
        v-model="hopLogic"
        :disabled="isSubmittingRequest"
        aria-label="Hop logic"
        data-track="tool_hop_logic_change"
        :data-track-props="JSON.stringify({ hopLogic })"
     v-if="!isRestricted"  >
        <option value="fifo">FIFO</option>
        <option value="lifo">LIFO</option>
        <option value="next_out">PIFO</option>
      </select>
<span
  class="help tip"
  tabindex="0"
  :data-tip="hopLogicHelp"
  aria-label="Hop logic help"
   v-if="!isRestricted"
>?</span>

    </div>

    <!-- Mode control -->
    <div class="modes-row__control">
      <label class="sr-only" for="mode">Mode</label>

      <select
        id="mode"
        v-model="mode"
        :disabled="isSubmittingRequest"
        aria-label="Mode"
        data-track="tool_mode_change"
        :data-track-props="JSON.stringify({ mode })"
      >
        <option value="bucket">Normal</option>
        <option value="normal">Advanced</option>
      </select>
    </div>
  </div>
</div>



            <div class="actions"><div class="submit-container">
             <button
  type="submit"
  :disabled="isSubmittingRequest"
  class="btn btn--primary"
  data-track="tool_submit_click"
  :data-track-props="
    JSON.stringify({
      has_to: !!toAddress,
      mode,
      tx_len: (transactionHash||'').length
    })
  "
>
  <span v-if="!isSubmittingRequest">Submit request</span>
  <span v-else>Submitting…</span>
</button></div>
            </div>
          </form>

          <transition name="fade">
            <div v-if="isSubmittingRequest" class="submitting">
              <div class="spinner md"></div>
              <p class="muted">Submitting request…</p>
            </div>
          </transition>
        </article>

        <!-- Legend (side panel on desktop) -->
        <aside class="card">
          <h2 class="card__title">Flag legend</h2>
          <ul class="flags">
            <li><img src="../assets/greenflag.png" alt="" class="flag" /> <strong>Green</strong> — likely compliant exchange</li>
            <li><img src="../assets/blueflag.png"  alt="" class="flag" /> <strong>Blue</strong> — funds remain at address</li>
            <li><img src="../assets/orangeflag.png" alt="" class="flag" /> <strong>Orange</strong> — complication; manual review</li>
            <li><img src="../assets/redflag.png"   alt="" class="flag" /> <strong>Red</strong> — unrecoverable / non‑compliant</li>
          </ul>
    <!-- in the legend card -->
<div class="legend-links">
 <router-link
  class="btn linkish"
  to="/BasicTutorial"
  data-track="open_tutorial_from_tool"
  :data-track-props="JSON.stringify({ source:'legend' })"
>
  How-To
</router-link>
<!--  <router-link class="btn linkish" to="/">What’s new</router-link>-->
</div>

          <div class="tiny muted space-top">
            Supports BTC, ETH, common ERC‑20s, and TRON USDT.  Other tokens like TON, Polygon, Base, XRP, NFTs are not supported.
            Queue is first‑come first-serve; jobs over 10 minutes may be truncated. History limit 150; max 100 pending.  Traces forward.
          </div>
         <!--  CSV / Txn History helper callout -->
     <div class="legend-helper space-top2"  v-if="!isRestricted">
      
    <div class="legend-helper__body">
      <div class="legend-helper__title">
        Have a CSV or transaction history?
      </div>
      <p class="legend-helper__text">
        Use the CSV Helper to scan your export and even make <b>batch requests</b>.
      </p>
    </div>

    
    <router-link
      class="btn legend-helper__cta"
      to="/TxnHistoryInterpreter"
      data-track="open_txn_history_interpreter"
      :data-track-props="JSON.stringify({ source:'legend' })"
    >
      Open CSV helper
    </router-link>



  </div>


         <!--  Case Prep Buddy callout -->
     <div class="legend-helper space-top2"  v-if="!isRestricted">
      
    <div class="legend-helper__body">
      <div class="legend-helper__title">
        Need more guidance?
      </div>
      <p class="legend-helper__text">
        Use <b>Case Prep Buddy</b> to get started fast.
      </p>
    </div>

    
    <router-link
      class="btn legend-helper__cta"
      to="/case-prep-buddy"
      data-track="case_prep_buddy"
      :data-track-props="JSON.stringify({ source:'legend' })"
    >
      Case Prep Buddy
    </router-link>






  </div>




        </aside>
        
      </section>




      <!-- To‑address picker modal -->
      <div v-if="showToAddressPicker" class="modal">
        <div class="modal__head">
          <strong>Select a valid To Address</strong>
          <button @click="showToAddressPicker = false" class="close">×</button>
        </div>
        <div class="modal__body">
      <p
  v-for="entry in toAddressSuggestions"
  :key="entry.address"
  class="choice"
  @click="chooseToAddress(entry.address)"
  data-track="tool_to_address_pick"
  :data-track-props="JSON.stringify({
    has_amt: !!entry.amount || !!entry.value,
    token: entry.token || null
  })"
>
  {{ entry.address }}
            <span v-if="entry.amount && entry.token && isRecognizedToken(entry.token)">
              — {{ formatAmount(entry.amount, tokenSymbols?.[entry.token?.toLowerCase?.()] || entry.token) }}
            </span>
            <span v-else-if="entry.value">
              — {{ formatValue(entry.value, entry.network || 'ethereum') }}
            </span>
          </p>
        </div>
      </div>

      <!-- Address help (transaction picker) -->
      <div v-if="showAddressHelpPanel" class="modal">
        <div class="modal__head">
          <strong>Select the transaction you sent</strong>
          <button @click="showAddressHelpPanel = false" class="close">×</button>
        </div>
        <div class="modal__body">
          <div v-for="tx in paginatedResults" :key="tx.hash" class="tx">
            <div class="tx__summary">
              <strong>{{ tx.amount }}</strong> received at {{ formatDateLocal(tx.timestamp) }}<br />
              From:
              <span class="short" :title="tx.from">{{ shorten(tx.from) }}</span>
            </div>
           <button
  class="btn btn--primary btn--sm"
  @click="selectSuggestedTransaction(tx)"
  data-track="tool_pick_suggested_tx"
  :data-track-props="JSON.stringify({
    has_amt: !!tx.amount,
    from_short: !!tx.from
  })"
>
  Select
</button>
          </div>

          <div class="muted tiny center space-top">
            Showing {{ resultsPerPageInAddressHelpPanel }} per page.
            {{ addressHelpResults.length >= 50
              ? 'Showing a maximum of 50 results. There may be more transactions not shown.'
              : `Total results: ${addressHelpResults.length}.` }}
          </div>

          <div class="pager">
          <button
  class="btn btn--ghost"
  @click="currentPageInAddressHelpPanel--"
  :disabled="currentPageInAddressHelpPanel === 1"
  data-track="tool_addrhelp_prev"
  :data-track-props="JSON.stringify({ page: currentPageInAddressHelpPanel - 1 })"
>‹ Prev</button>
            <span class="muted tiny">{{ currentPageInAddressHelpPanel }} / {{ totalPagesInAddressHelpPanel }}</span>
          <button
  class="btn btn--ghost"
  @click="currentPageInAddressHelpPanel++"
  :disabled="currentPageInAddressHelpPanel >= totalPagesInAddressHelpPanel"
  data-track="tool_addrhelp_next"
  :data-track-props="JSON.stringify({ page: currentPageInAddressHelpPanel + 1 })"
>Next ›</button>
          </div>

          <div v-if="addressHelpResults.length >= 50" class="tiny center space-top">
            Not seeing it?
            <a :href="getExplorerUrlForAddress(addressUsedForAssistance, inferredNetwork)" target="_blank" rel="noopener noreferrer">
              View full history on {{ inferredNetwork && inferredNetwork.charAt(0).toUpperCase() + inferredNetwork.slice(1) }} →
            </a>
          </div>
        </div>
      </div>

      <!-- Requests table -->
      <section class="card">
        <div class="card__header">
          <h2>Your requests</h2>
        </div>

        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Request ID</th>
                <th>TX</th>
                <!--<th>Type</th>-->
                <th>Mode</th>
                <th>Status</th>
                <th>Time</th>
                <th>Result</th>
                <th class="th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="request in requests" :key="request.request_id">
                <td>{{ request.request_id }}</td>

                <!-- Truncated w/ copy on click -->
                <td class="txcell" :data-full-hash="request.transaction_hash">
                  <span class="mono truncate" :title="request.transaction_hash">{{ request.transaction_hash }}</span>
                  <br />
                  <span class="muted mono truncate" v-if="request.to_address">{{ request.to_address }}</span>
                </td>

              <!--  <td>{{ request.request_type }}</td> -->
                <td>{{ getModeDisplay(request.mode) }} {{ getHopLogicDisplay(request.hop_logic) }}</td>
                <td>{{ request.status }}</td>
                <td>{{ request.request_time }}</td>

              <td>
 <button
  v-if="request.result && request.status === 'complete'"
  class="btn btn--ghost btn--sm"
  @click="openReport(request)"
  data-track="tool_view_report_click"
  :data-track-props="JSON.stringify({ id: request.request_id })"
>
  View report
</button>
  <span v-else class="muted tiny">Pending</span>
</td>
<td class="td-actions">
  <div class="actions-row">
    <!-- Primary: Screenshot -->
    <button
      v-if="request.status === 'complete'"
      class="btn btn--image btn--sm"
      @click="viewScreenshot(request.request_id)"
      data-track="tool_view_screenshot_click"
      :data-track-props="JSON.stringify({ id: request.request_id })"
    >
      Screenshot
    </button>

    <!-- Primary: Investigation -->
    <button
      class="btn btn--cta btn--sm"
      @click="goToInvestigation(request)"
      data-track="tool_request_investigation_click"
      :data-track-props="JSON.stringify({ id: request.request_id })"
      title="Request a professional investigation"
    >
      Investigate
    </button>

    <!-- Secondary actions in a dropdown -->
    <details
      class="action-menu"
      ref="actionMenus"
      @toggle="onActionMenuToggle($event)"
    >
      <summary
        class="btn btn--ghost btn--sm"
        data-track="tool_actions_more_open"
        :data-track-props="JSON.stringify({ id: request.request_id })"
      >
        More
      </summary>

      <div class="action-menu__panel" role="menu">
        <button
          v-if="request.status === 'complete' && request.mode === 'normal'"
          class="btn btn--ghost btn--sm menu-item"
          @click="onMenuCreateReport(request, $event)"
          role="menuitem"
        >
          Create report
        </button>

        <button
          class="btn btn--danger btn--sm menu-item"
          @click="onMenuDelete(request, $event)"
          role="menuitem"
        >
          Delete
        </button>

        <button
          v-if="!request.bug_reported"
          class="btn btn--bug btn--sm menu-item"
          @click="onMenuBugReport(request, $event)"
          role="menuitem"
        >
          Report bug
        </button>
      </div>
    </details>

    <!-- Keep bug status visible (tiny) -->
    <span
      v-if="request.bug_reported && request.bug_status !== 'resolved'"
      class="pill pill--warn tiny"
    >
      Bug reported
    </span>
    <span v-if="request.bug_status === 'resolved'" class="pill pill--ok tiny">
      Bug resolved
    </span>
  </div>
</td>



              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pager pager--table">
        <button
  class="btn btn--ghost"
  @click="previousPage"
  :disabled="currentPage === 1"
  data-track="tool_requests_prev"
  :data-track-props="JSON.stringify({ page: currentPage - 1 })"
>‹ Previous</button>
          <span class="muted">{{ currentPage }} / {{ totalPages }}</span>
          <button
  class="btn btn--ghost"
  @click="nextPage"
  :disabled="currentPage === totalPages"
  data-track="tool_requests_next"
  :data-track-props="JSON.stringify({ page: currentPage + 1 })"
>Next ›</button>
        </div>
      </section>

<!-- Credit usage confirmation modal -->
<transition name="fade">
  <div
    v-if="showCreditCostModal"
    class="modal-overlay modal-overlay--floating"
    aria-modal="true"
    role="dialog"
  >
    <section class="modal modal--credits">
      <header class="modal__head">
        <h2 class="card__title">Confirm credit usage</h2>
        <button
          type="button"
          class="close"
          @click="onCreditModalCancel"
          aria-label="Close credit confirmation"
        >
          ×
        </button>
      </header>

      <div class="modal__body">
        <!-- Loading state while fetching pricing -->
        <div v-if="creditModalBusy" class="submitting">
          <div class="spinner md"></div>
          <p class="small muted">Fetching current credit pricing…</p>
        </div>

        <!-- Error state if pricing fetch failed -->
        <div v-else-if="creditModalError">
          <p class="error">{{ creditModalError }}</p>
        </div>

        <!-- Normal state with pricing -->
        <div v-else-if="creditPricing">
          <p>
            This analysis will use
            <strong v-if="discountIsActive">
              {{ effectiveCreditCost }} credit{{ effectiveCreditCost === 1 ? '' : 's' }}
            </strong>
            <strong v-else>
              {{ creditPricing.baseCost }} credit{{ creditPricing.baseCost === 1 ? '' : 's' }}
            </strong>
            from your balance.
          </p>

          <!-- Discount details -->
          <p v-if="discountIsActive" class="small">
            Standard cost:
            <s>{{ creditPricing.baseCost }} credit{{ creditPricing.baseCost === 1 ? '' : 's' }}</s><br>
            Current discount:
            <strong>{{ creditPricing.discountLabel || 'Limited-time discount' }}</strong><br>
            Discounted cost for this request:
            <strong>{{ effectiveCreditCost }} credit{{ effectiveCreditCost === 1 ? '' : 's' }}</strong><br>
            <span v-if="discountTimeLeftLabel" class="muted">
              {{ discountTimeLeftLabel }}
            </span>
          </p>

          <!-- No discount -->
          <p v-else class="small muted">
            Credits are deducted when you
            submit.
          </p>

          <p class="tiny muted">
            PathTracer uses a pay-per-request credit system for non-subscription accounts.
            Once a request is accepted into the queue, the credits used for it are
            not refundable, even if you later decide not to use the result, or delete the result from your results history.
          </p>

          <div class="disclaimer__ack">
            <label class="checkbox">
              <input type="checkbox" v-model="creditModalDontRemind">
              <span>Do not show this reminder again on this device.</span>
            </label>
          </div>
        </div>
      </div>

      <footer class="modal__actions">
        <button type="button" class="btn btn--ghost" @click="onCreditModalCancel">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn--primary"
          :disabled="creditModalBusy || !creditPricing"
          @click="onCreditModalConfirm"
        >
          Confirm and run request
        </button>
      </footer>
    </section>
  </div>
</transition>



    </div>
  </main>
</template>

<script>
import axios from 'axios';
import AuthService from '@/services/AuthService';
import SpreadsheetComponent from "@/components/SpreadsheetComponent.vue";
export default {
  name: 'AnalysisTool',
   components: { SpreadsheetComponent },
  data() {
    return {
      hopLogicHelp: `Hop logic controls how PathTracer chooses the next hop.

FIFO: oldest funds move first.
LIFO: newest funds move first.
PIFO: next funds out are followed.`, // first outbound after receipt

       boundCloseActionMenus: null,
    boundCloseActionMenusOnKey: null,
    isRestricted: false,
    restrictionReason: '',
    restrictionUntil: null,
          // Credit confirmation modal state
      showCreditCostModal: false,
      creditModalBusy: false,
      creditModalError: '',
      creditPricing: null,        // { baseCost, discountCost, discountLabel, discountStartsUtc, discountEndsUtc, serverNowUtc }
      creditModalDontRemind: false,
      skipCreditConfirm: false,   // persisted per device
showDeleteConfirm: false,
  deleteRequestTarget: null,
  prefillMsg: '', // small banner to reassure the user
        // Advanced-mode helpers
    showAdvancedSheet: false,
    advancedHeaders: [],
    advancedRows: [],
    advancedRowCount: 0,
    advancedColCount: 0,
    advancedDateRange: null, // { min: 'YYYY-MM-DD', max: 'YYYY-MM-DD' }
    disclaimerOpen: true,
      transactionHash: '',
      requestType: 'single',
      mode: 'bucket',
    // New: hop logic (default FIFO for compatibility)
    hopLogic: 'fifo',
      requests: [],
        toAddress: '', // Optional to address
      isLoggedIn: false,
      validationError: '',
      notificationMessage: '',    // Notification message text
      notificationType: '',       // Type of notification (e.g., success or error)
      lastStatuses: {}, // Track last known statuses to detect changes
      username: '',
      userRole: '',
      currentPage: 1,
      totalRequests: 0,
      limit: 10,
      canUseNormalMode: false, 
      pollErrorCount: 0,
      showToAddressPicker: false,
      toAddressSuggestions: [],
      tokenSymbols: {}, 
creditBalance: null,
isSubscriptionUser: false,
isUnlimitedUser: false,
subscriptionExpiresAt: null,
subscriptionDays: 0,
role: null,
loading: true,
isSubmittingRequest: false,
addressHelpResults: [],
    showReportModal: false,
    reportRequest: null,
showAddressHelpPanel: false,
 currentPageInAddressHelpPanel: 1,
    resultsPerPageInAddressHelpPanel: 10,
    addressUsedForAssistance: null,
    copiedReport: false,
    copyingReport: false,
    copiedReportTimer: null,
      showBugConfirm: false,
    bugRequest: null,
    };
  },
watch: {
  // If the route changes while the modal is open, close it cleanly
  $route() {
    if (this.showReportModal) this.showReportModal = false;
  }
},
  computed: {

     discountIsActive() {
    if (!this.creditPricing) return false;
    const { discountCost, discountStartsUtc, discountEndsUtc, serverNowUtc } = this.creditPricing;
    if (discountCost == null) return false;
    if (!serverNowUtc) return false;

    const now = new Date(serverNowUtc);
    if (discountStartsUtc) {
      const start = new Date(discountStartsUtc);
      if (now < start) return false;
    }
    if (discountEndsUtc) {
      const end = new Date(discountEndsUtc);
      if (now > end) return false;
    }
    return true;
  },

  effectiveCreditCost() {
    if (!this.creditPricing) return null;
    if (!this.discountIsActive) return this.creditPricing.baseCost;
    const d = this.creditPricing.discountCost;
    return d != null ? d : this.creditPricing.baseCost;
  },

  discountTimeLeftLabel() {
    if (!this.creditPricing || !this.discountIsActive || !this.creditPricing.discountEndsUtc) {
      return '';
    }
    const now = new Date(this.creditPricing.serverNowUtc);
    const end = new Date(this.creditPricing.discountEndsUtc);
    const diffMs = end - now;
    if (diffMs <= 0) return '';

    const hours = Math.round(diffMs / (1000 * 60 * 60));
    if (hours < 24) {
      return `${hours} hour${hours === 1 ? '' : 's'} left on this promo`;
    }
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
    return `${days} day${days === 1 ? '' : 's'} left on this promo`;
  },
  humanReport() {
    // Normal mode -> existing interpreter
    if (!this.isAdvancedReport) return this.interpretReport(this.parsedReport);

    // Advanced mode -> friendly summary lines
    return this.buildAdvancedHumanReport();
  },
  isAdvancedReport() {
    const mode = this.reportRequest?.mode;
    // Primary signal: your mode mapping (anything not 'bucket' is Advanced)
    const modeSaysAdvanced = !!mode && mode !== 'bucket';

    // Safety net: sniff content for TSV headers in case historical records
    // were mis-labeled but contain Advanced TSV output.
    const text = this.reportRequest?.result || "";
    const firstLine = text.split(/\r?\n/, 1)[0] || "";
    const looksTSV =
      firstLine.includes("\t") &&
      /(Label|Hash|FromAddress|ToAddress|Date)/i.test(firstLine);

    return modeSaysAdvanced || looksTSV;
  },
parsedReport() {
    // Normal mode only
    if (this.isAdvancedReport) return null;
    return this.parseReport(this.reportRequest?.result || "");
  },

      capitalizedNetwork() {
    if (!this.inferredNetwork) return '';
    return this.inferredNetwork.charAt(0).toUpperCase() + this.inferredNetwork.slice(1);
  },
 paginatedResults() {
    const start = (this.currentPageInAddressHelpPanel - 1) * this.resultsPerPageInAddressHelpPanel;
    return this.addressHelpResults.slice(start, start + this.resultsPerPageInAddressHelpPanel);
  },
  totalPagesInAddressHelpPanel() {
    return Math.ceil(this.addressHelpResults.length / this.resultsPerPageInAddressHelpPanel);
  },
  isInfiniteAccess() {
    return this.role === 'admin' || this.role === 'moderator' || this.isUnlimitedUser;
  },
    totalPages() {
  const pages = Math.ceil(this.totalRequests / this.limit);
  return pages > 0 ? pages : 1;
},
  },


  methods: {
      closeAllActionMenus() {
    // Vue gives array for refs in v-for; sometimes it’s a single element.
    const menus = this.$refs.actionMenus;
    if (!menus) return;

    const list = Array.isArray(menus) ? menus : [menus];
    for (const el of list) {
      if (el && el.hasAttribute('open')) el.removeAttribute('open');
    }
  },

  // Close on click-away (ignore clicks inside any action-menu)
  closeActionMenusOnOutsideClick(e) {
    // If the click happened inside any action-menu, do nothing.
    const clickedInsideMenu = e.target && e.target.closest && e.target.closest('.action-menu');
    if (clickedInsideMenu) return;

    this.closeAllActionMenus();
  },

  // Optional nicety: only allow one menu open at a time.
  onActionMenuToggle(e) {
    const detailsEl = e.target;
    if (!detailsEl || detailsEl.tagName !== 'DETAILS') return;

    // If this menu just opened, close all others.
    if (detailsEl.open) {
      const menus = this.$refs.actionMenus;
      const list = Array.isArray(menus) ? menus : [menus];
      for (const el of list) {
        if (el && el !== detailsEl && el.hasAttribute('open')) {
          el.removeAttribute('open');
        }
      }
    }
  },

    goToInvestigationFromReport() {
  const r = this.reportRequest;
  if (!r) return;

  this.closeReport();

  this.$router.push({
    name: 'RequestInvestigation',
    params: { requestId: String(r.request_id) },
    query: {
      tx: r.transaction_hash || '',
      to: r.to_address || '',
      mode: r.mode || '',
    },
  });
},

    closeActionMenuFromEvent(e) {
  const details = e && e.currentTarget ? e.currentTarget.closest('details') : null;
  if (details) details.removeAttribute('open');
},

onMenuCreateReport(request, e) {
  this.closeActionMenuFromEvent(e);
  this.createReport(request);
},

onMenuDelete(request, e) {
  this.closeActionMenuFromEvent(e);
  this.askDeleteConfirm(request);
},

onMenuBugReport(request, e) {
  this.closeActionMenuFromEvent(e);
  this.askBugReport(request);
},

    goToInvestigation(request) {
  const id = request?.request_id ? String(request.request_id) : '';

  this.$router.push({
    name: 'RequestInvestigation',
    params: { requestId: id },
    query: {
      tx: request?.transaction_hash || '',
      to: request?.to_address || '',
      mode: request?.mode || '',
    },
  });
},

   // -------------------------
  // Delete confirm flow
  // -------------------------
  askDeleteConfirm(request) {
    this.deleteRequestTarget = request;
    this.showDeleteConfirm = true;

    this.$nextTick(() => {
      // Optional: focus the modal container for immediate ESC key handling
      if (this.$refs.deleteConfirmModal) {
        this.$refs.deleteConfirmModal.focus();
      }
    });
  },

  onDeleteCancel() {
    this.showDeleteConfirm = false;
    this.deleteRequestTarget = null;
  },

  async doDeleteConfirmed() {
    if (!this.deleteRequestTarget?.request_id) return;

    const id = this.deleteRequestTarget.request_id;

    // Close first so UI feels immediate
    this.showDeleteConfirm = false;
    this.deleteRequestTarget = null;

    // Call your existing delete logic (rename recommended below)
    await this.deleteRequestNow(id);
  },



  // -------------------------
  // External link button helpers
  // -------------------------
  showExternalLinkFor(request) {
    // Customize as you like (example: only show when complete)
    return !!request?.transaction_hash;
  },

  externalLinkFor(request) {
  // Replace with your real external URL format.
    // This is just a safe example that passes tx hash and request id.
    const tx = encodeURIComponent(request.transaction_hash || '');
    const rid = encodeURIComponent(String(request.request_id || ''));
    return `https://external-site.example.com/lookup?tx=${tx}&rid=${rid}`;
  },
     _cleanAlnum(s, maxLen) {
      if (!s) return '';
      const out = String(s).replace(/[^A-Za-z0-9]/g, '');
      return typeof maxLen === 'number' ? out.slice(0, maxLen) : out;
    },

    prefillFromSession() {
      let raw = null;
      try { raw = sessionStorage.getItem('pt:analysisPrefill'); } catch (_) { console.log("catch1"); }
      if (!raw) return;

      let obj = null;
      try { obj = JSON.parse(raw); } catch (_) { obj = null; }

      if (!obj || (typeof obj !== 'object')) {
        try { sessionStorage.removeItem('pt:analysisPrefill'); } catch (_) { console.log("catch2");  }
        return;
      }

      // Sanitize to your input patterns
      const tx = this._cleanAlnum(obj.tx || '', 66);
      const to = this._cleanAlnum(obj.to || '', 64);

      if (tx) this.transactionHash = tx;
      if (to) this.toAddress = to;

      // Optional: tiny heads-up for the user
      if (tx || to) this.prefillMsg = 'Fields prefilled from CSV Helper.';

      // Clear immediately to avoid stale data on subsequent visits
      try { sessionStorage.removeItem('pt:analysisPrefill'); } catch (_) {console.log("catch3"); }

      // If you ever want auto-submit when tx (and optionally to) are present:
      // if (tx) this.handleSubmitRequest();
    },

     handleSubmitRequest() {
    if (this.isSubmittingRequest) return; // existing guard

    // If this user is not on the credit system, or has asked not to see the reminder,
    // just submit as usual.
    if (!this.shouldShowCreditModalForThisUser()) {
      this.submitRequest();
      return;
    }

    // Otherwise, fetch pricing and open the modal.
    this.openCreditConfirmModal();
  },

  // Decide whether this user should see the credit modal
   shouldShowCreditModalForThisUser() {
    // Local setting: user has chosen "don't remind me"
    if (this.skipCreditConfirm) return false;

    // Unlimited users never spend per-request credits
    if (this.isUnlimitedUser) return false;

    // Active subscription users are not on pure credit billing
    if (this.isSubscriptionUser) return false;

    // If we have not yet fetched credit info, don't block with a modal.
    // (The request will still go through your existing access checks.)
    if (this.creditBalance === null) return false;

    // If they have no credits, your existing access gate will fire;
    // no need to show the spending modal on top of that.
    if (this.creditBalance <= 0) return false;

     //   const privilegedRoles = ['admin', 'moderator'];
   // if (privilegedRoles.includes(this.role)) return false;


    // At this point: non-unlimited, non-subscription, known positive credit balance
    // -> pure credit-based usage, so show the confirmation modal.
    return true;
  },

  // Fetch platform settings / pricing and open the modal
 async openCreditConfirmModal() {
  this.creditModalError = '';
  this.creditPricing = null;
  this.creditModalBusy = true;
  this.showCreditCostModal = true;

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Not authenticated');
    }

    const res = await axios.get('/etherscan/fetch_platform_settings.php', {
      params: {
        context: 'main_analysis',
        mode: this.mode, // "bucket" (Normal), "normal" (Advanced), or "linkcheck"
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = res.data || {};
    if (data.status !== 'success' || !data.pricing) {
      throw new Error(data.message || 'Missing pricing data');
    }

    const p = data.pricing || {};

    this.creditPricing = {
      baseCost: Number(p.base_cost ?? p.baseCredits ?? 0),
      discountCost:
        p.discount_cost != null
          ? Number(p.discount_cost)
          : p.discountCredits != null
          ? Number(p.discountCredits)
          : null,
      discountLabel: p.discount_label || p.discountLabel || '',
      discountStartsUtc: p.discount_starts_utc || p.discountStartsUtc || null,
      discountEndsUtc: p.discount_ends_utc || p.discountEndsUtc || null,
      serverNowUtc: data.server_now_utc || data.serverNowUtc || null,
    };

    if (!this.creditPricing.baseCost || !this.creditPricing.serverNowUtc) {
      throw new Error('Incomplete pricing response');
    }

    if (
      this.creditPricing.discountCost != null &&
      this.creditPricing.discountCost >= this.creditPricing.baseCost
    ) {
      this.creditPricing.discountCost = null;
    }
  } catch (err) {
    console.error('Failed to load credit pricing:', err);
    this.showCreditCostModal = false;
    this.errorMessage =
      'Could not fetch the current credit cost for this request. Please try again in a moment.';
  } finally {
    this.creditModalBusy = false;
  }
},


  // User clicked "Cancel" in the modal
  onCreditModalCancel() {
    this.showCreditCostModal = false;
    this.creditModalError = '';
    this.creditPricing = null;
  },

  // User confirmed and wants to run the request
  onCreditModalConfirm() {
    if (!this.creditPricing || this.creditModalBusy) return;

    // Persist the "do not show again" choice only if they actually confirm.
    if (this.creditModalDontRemind) {
      this.skipCreditConfirm = true;
      localStorage.setItem('pt_skip_credit_confirm', '1');
    } else {
      this.skipCreditConfirm = false;
      localStorage.removeItem('pt_skip_credit_confirm');
    }

    this.showCreditCostModal = false;

    // Now run your existing request logic
    this.submitRequest();
  },
      // ---------- Advanced mode helpers ----------
  buildAdvancedHumanReport() {
    const out = [];

    // Parse TSV once to compute counts/date range for the summary
    const tsv = this.reportRequest?.result || "";
    const { headers, rows } = this.parseTSV(tsv);
    this.advancedHeaders = headers;
    this.advancedRows = rows;
    this.advancedRowCount = rows.length;
    this.advancedColCount = headers.length;
    this.advancedDateRange = this.computeDateRange(headers, rows);

    // Headline
    out.push({
      text:
        `Advanced analysis complete — spreadsheet output with ` +
        `${this.advancedRowCount.toLocaleString()} row${this.advancedRowCount === 1 ? "" : "s"} ` +
        `and ${this.advancedColCount} column${this.advancedColCount === 1 ? "" : "s"}.`
    });

    // Date range, if detected
    if (this.advancedDateRange?.min && this.advancedDateRange?.max) {
      const { min, max } = this.advancedDateRange;
      out.push({ text: `Date range detected: ${min} → ${max}.` });
    }

    // Quick usage tip
    out.push({
      text:
        "Tip: Click “Open spreadsheet preview” to browse the results here, " +
        "or copy/download and paste into cell A1 in your spreadsheet app."
    });

    return out;
  },

  parseTSV(tsvText) {
    const text = (tsvText || "").trim();
    if (!text) return { headers: [], rows: [] };

    const lines = text.split(/\r?\n/);
    if (!lines.length) return { headers: [], rows: [] };

    const headers = (lines[0] || "").split("\t");
    const rows = lines.slice(1).map(line => line.split("\t"));

    return { headers, rows };
  },

  computeDateRange(headers, rows) {
    const dateIdx = headers.findIndex(h => /date/i.test(h));
    if (dateIdx === -1) return null;

    let min = null;
    let max = null;

    for (const r of rows) {
      const raw = r[dateIdx];
      // Expecting YYYY-MM-DD or ISO-ish; keep simple: grab first 10 chars
      const ymd = (raw || "").slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) continue;
      if (!min || ymd < min) min = ymd;
      if (!max || ymd > max) max = ymd;
    }
    return (min && max) ? { min, max } : null;
  },

 ensureAdvancedSheetLoaded() {
    const tsv = this.reportRequest?.result || "";
    const { headers, rows } = this.parseTSV(tsv);
    this.advancedHeaders = headers;
    this.advancedRows = rows;
    const grid = [headers, ...rows];

    // Show the component first; then load once it exists
    if (!this.showAdvancedSheet) {
      this.showAdvancedSheet = true;
      this.$nextTick(() => {
        const comp = this.$refs.advancedSheet;
        if (comp?.loadData) comp.loadData(grid);
        // Handsontable sometimes needs a render after first show in a modal
        comp?.render?.();
      });
    } else {
      // Already mounted -> load immediately
      const comp = this.$refs.advancedSheet;
      if (comp?.loadData) comp.loadData(grid);
      comp?.render?.();
    }
  },
 copyRawTSV() {
    const tsv = this.reportRequest?.result || "";
    if (!tsv) return;
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(tsv);
    } else {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = tsv;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
    }
  },

  downloadAdvanced(format = "tsv") {
    const raw = this.reportRequest?.result || "";
    if (!raw) return;

    const blob =
      format === "csv"
        ? new Blob([this.tsvToCSV(raw)], { type: "text/csv;charset=utf-8" })
        : new Blob([raw], { type: "text/tab-separated-values;charset=utf-8" });

    const filename = `advanced-report.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  tsvToCSV(tsv) {
    // Simple TSV->CSV: split rows, split cols by \t, escape quotes, join with commas
    const lines = (tsv || "").split(/\r?\n/);
    return lines
      .map(line =>
        line
          .split("\t")
          .map(cell => {
            const s = String(cell ?? "");
            // Escape " by doubling them, wrap in quotes if needed
            const needsQuotes = /[",\n]/.test(s);
            const escaped = s.replace(/"/g, '""');
            return needsQuotes ? `"${escaped}"` : escaped;
          })
          .join(",")
      )
      .join("\n");
  },

  askBugReport(request) {
    this.bugRequest = request;
    this.showBugConfirm = true;
    this.$nextTick(() => this.$refs.bugConfirmModal?.focus());
  },
  doReportBug() {
    if (this.bugRequest) this.reportBug(this.bugRequest);
    this.showBugConfirm = false;
    this.bugRequest = null;
  },


     async copyReport() {
    try {
      this.copyingReport = true;
      // Prefer copying the human-friendly interpretation; fall back to raw if needed.
      const human = Array.isArray(this.humanReport)
        ? this.humanReport.map(l => l.text).join("\n")
        : String(this.humanReport || "");
      const raw = String(this.reportRequest?.result || "");
      const textToCopy = human?.trim()
        ? human + (raw?.trim() ? `\n\n--- Raw Report ---\n${raw}` : "")
        : raw;

      // Try modern clipboard API first
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = textToCopy;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      // Visual feedback
      this.copiedReport = true;
      if (this.copiedReportTimer) clearTimeout(this.copiedReportTimer);
      this.copiedReportTimer = setTimeout(() => {
        this.copiedReport = false;
        this.copiedReportTimer = null;
      }, 1600);
    } catch (e) {
      // Optional: toast/notice – keep silent if you prefer
      console.warn("Copy failed:", e);
    } finally {
      this.copyingReport = false;
    }
  },
     getFlagIcon(flag) {
    switch (flag) {
      case "green":  return require("../assets/greenflag.png");
      case "blue":   return require("../assets/blueflag.png");
      case "orange": return require("../assets/orangeflag.png");
      case "red":    return require("../assets/redflag.png");
      default:       return null;
    }
  },
    parseReport(result) {
    if (!result || /error/i.test(result)) {
      return { error: true };
    }

    // Normalize and split
    const lines = result
      .split("\n")
      .map(l => l.trim())
      .filter(l => l.length);

    const flagRe = /^(Green|Blue|Orange|Red)\s+flags?\s*-\s*(.+)$/i;
    const initRe = /^Initially followed:\s*(.+)$/i;

    const flags = [];
    const seenFlagKeys = new Set();
    const initialCandidates = [];

    for (const line of lines) {
      const f = line.match(flagRe);
      if (f) {
        const flag = f[1].toLowerCase();
        const amount = f[2].trim(); // e.g. "2225.923 USDT" or "1.87 ETH"
        const key = `${flag}|${amount}`.toLowerCase();

        // De-duplicate exact same flag+amount (handles full report repeats)
        if (!seenFlagKeys.has(key)) {
          seenFlagKeys.add(key);
          flags.push({ flag, amount });
        }
        continue;
      }

      const i = line.match(initRe);
      if (i) {
        initialCandidates.push(i[1].trim());
      }
    }

    // Determine initial
    let initial = null;
    let notes = { deduped: false, duplicateInitialCount: 0, conflictingInitials: false };

    if (initialCandidates.length > 0) {
      // If multiple, keep the first; track duplicates and conflicts
      initial = initialCandidates[0];
      notes.duplicateInitialCount = initialCandidates.length;
      const uniqueInits = Array.from(new Set(initialCandidates.map(s => s.toLowerCase())));
      if (uniqueInits.length > 1) {
        notes.conflictingInitials = true;
      }
      if (initialCandidates.length > 1) {
        notes.deduped = true;
      }
    }

    // If we saw any repeated lines (either by duplicate flags or multiple initial lines), mark deduped
    if (seenFlagKeys.size < flags.length || initialCandidates.length > 1) {
      notes.deduped = true;
    }

    return { flags, initial, error: false, notes };
  },

  interpretReport(parsed) {
      // Advanced mode is handled elsewhere
  if (this.isAdvancedReport) return this.buildAdvancedHumanReport();
  
  // Always return an array of { icon?, text } objects to render with flag icons
    const out = [];

    if (parsed.error) {
      out.push({
        text: "Something went wrong with this report. Please press Report Bug and request a refund for the credit used."
      });
      return out;
    }

    // Warn if we detected duplication or conflicts
    if (parsed.notes?.deduped) {
      const base = parsed.notes.conflictingInitials
        ? "We found repeated and conflicting lines in the report. We interpreted the first occurrence."
        : "We found repeated lines in the report. We removed duplicates and interpreted the first occurrence.";
      out.push({ text: base + " If this looks off, please press Report Bug and request a refund." });
    }

    // No flags? Treat as error per your rule set.
    if (!parsed.flags || parsed.flags.length === 0) {
      const initText = parsed.initial ? ` of ${parsed.initial}` : "";
      out.push({
        text: `The analysis only shows an initially followed amount${initText}. This indicates something went wrong. Please report a bug and request a refund.  To report a bug, you can press Report Bug beside the request in your Request History below.`
      });
      return out;
    }

    // Initial line (if present)
    if (parsed.initial) {
      out.push({ text: `The analysis began by following ${parsed.initial}.` });
    }

    // Flag lines with your flag icons (no emojis)
    for (const f of parsed.flags) {
      let message;
      switch (f.flag) {
        case "green":
          message = `${f.amount} arrived at a likely compliant exchange or other subpoenable entity.`; break;
        case "blue":
          message = `${f.amount} remains in a dormant wallet.`; break;
        case "orange":
          message = `${f.amount} requires manual review due to complications.`; break;
        case "red":
          message = `${f.amount} reached a non-compliant or unrecoverable destination.`; break;
        default:
          message = `${f.amount} was flagged (${f.flag}).`; break;
      }
      out.push({ flag: f.flag, icon: this.getFlagIcon(f.flag), text: message });
    }

    // Multi-token swap note: detect different symbols (last token in the amount string)
    if (parsed.flags.length > 1) {
      const tokens = parsed.flags.map(f => {
        const parts = f.amount.split(/\s+/);
        return parts[parts.length - 1]; // ETH, USDT, BTC, etc.
      });
      if (new Set(tokens.map(t => t.toUpperCase())).size > 1) {
        out.push({ text: "Different token types appear in the outcome, which suggests swaps occurred along the way." });
      }
    }

    return out;
  },
 openReport(req) {
    this.reportRequest = req;
    this.showReportModal = true; // needed?
      this.showAdvancedSheet = false;
    this.advancedHeaders = [];
    this.advancedRows = [];
    this.advancedRowCount = 0;
    this.advancedColCount = 0;
    this.advancedDateRange = null;
    this.$nextTick(() => {
      this.$refs.reportModal?.focus(); // keyboard focus to modal
    });
  },
  closeReport() {
    this.showReportModal = false;
    this.reportRequest = null;
  },
  openScreenshotAndClose(requestId) {
    // Close modal first to avoid any overlay remaining during navigation
    this.showReportModal = false;
    this.$nextTick(() => this.viewScreenshot(requestId));
  },

  reportLocalTime(iso) {
    const d = new Date(iso);
    return isNaN(d) ? '' : d.toLocaleString();
  },
  getExplorerUrlForAddress(address, network) {
  switch (network) {
    case 'bitcoin':
      return `https://mempool.space/address/${address}`;
    case 'ethereum':
      return `https://etherscan.io/address/${address}`;
    case 'tron':
      return `https://tronscan.org/#/address/${address}`;
    default:
      return '';
  }
}
,
selectSuggestedTransaction(tx) {
  this.transactionHash = tx.hash;
  this.toAddress = tx.to;
  this.showAddressHelpPanel = false;
  this.showNotification('Transaction selected. You may now submit the request.', 'success');
}
,
async enterAddressAssistanceMode(address) {
  this.showNotification("Looking up transactions to this address...", "info");
  this.isSubmittingRequest = true;

  try {
    const res = await fetch("/api/etherscan/lookup_address_for_transaction_candidates.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ address, limit: 50 })
    });

    const data = await res.json();
    if (Array.isArray(data)) {
      this.addressHelpResults = data;
      this.addressUsedForAssistance = address;
this.currentPageInAddressHelpPanel = 1;
      this.showAddressHelpPanel = true;
      this.inferredNetwork = this.addressHelpResults.length > 0
  ? this.addressHelpResults[0].network
  : null;

    } else {
      this.showNotification("Could not find transactions to that address.", "error");
    }
  } catch (e) {
    this.showNotification("Lookup failed.", "error");
  } finally {
    this.isSubmittingRequest = false;
  }
}
,
sendAnalysisRequest(username) {
  const requestData = {
    username: username,
    transaction_hash: this.transactionHash,
    to_address: this.toAddress,
    request_type: this.requestType,
    mode: this.mode,
    hop_logic: this.hopLogic
  };
  this.showToAddressPicker = false;
this.toAddressSuggestions = [];

  axios.post('etherscan/create_request.php', requestData)
    .then(response => {
      if (response.data.status === 'success') {
        this.fetchRequests();
        this.showNotification('Request submitted successfully!', 'success');
        this.validationError = '';
        this.clearForm();
        
  this.refreshUserCredits(); 
      } else {
        this.showNotification(response.data.message, 'error');
      }
    })
    .catch(error => {
     this.showNotification('Request submission error. Please try again.', 'error');
      console.error('Request submission error:', error);
    })
//   .catch(error => {
//  const msg =
//    error?.response?.data?.message ||
 //   'Request submission error. Please try again.';
//  this.showNotification(msg, 'error');
//  console.error('Request submission error:', error);
//})

  .finally(() => {
    this.isSubmittingRequest = false;
  });
}
,


 isValidAddress(address) {
        const patterns = {
            'Ethereum': /^0x[a-fA-F0-9]{40}$/,
            'Bitcoin': /^(bc1([a-zA-HJ-NP-Z0-9]{6,87})|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/,
            'Tron': /^T[a-zA-Z0-9]{33}$/
        };
        const network = this.detectNetwork(address);
        if (!network) return false; // If the network isn't detected, return false
        return patterns[network].test(address);
    },
    detectNetwork(address) {
        if (address.startsWith('0x') && address.length === 42) {
            return 'Ethereum';
        } else if ((address.startsWith('bc1') && address.length >= 14 && address.length <= 62) || 
                   ((address.startsWith('1') || address.startsWith('3')) && address.length >= 26 && address.length <= 35)) {
            return 'Bitcoin';
        } else if (address.startsWith('T') && address.length === 34) {
            return 'Tron';
        }
        return null;
    },
     viewScreenshot(requestId) {
    const url = this._resolveScreenshotUrl(requestId);
    this._openInNewTab(url);
  },
      _resolveScreenshotUrl(requestId) {
    const route = { name: 'ViewImage', params: { requestID: String(requestId) } };
    const { href } = this.$router.resolve(route);
    return href.startsWith('http') ? href : `${window.location.origin}${href}`;
  },
  _openInNewTab(url) {
    // Create a hidden <a target="_blank"> and click it – avoids several
    // false negatives you get from window.open across browsers.
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener'; // avoid noreferrer to reduce null-return quirks
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // We can't reliably detect success; do not auto-fallback.
  },

  /*
 viewScreenshot(requestID) {
  //(`Navigating to screenshot for request ID: ${requestID}`); // Add logging here
 this.$router.push({ name: 'ViewImage', params: { requestID } });


  },*/
    checkLogin() {
      const username = localStorage.getItem('username');
      this.isLoggedIn = !!username;
      this.username = username;
    //  console.log('Tool42Page checkLogin: Username in localStorage:', username);
      return username;
    },
     reportBug(request) {
    const postData = {
        request_id: request.request_id,
        username: this.username,
            description: `Bug report for request ${request.request_id} related to transaction hash: ${request.transaction_hash}`
  
    };
    axios.post('etherscan/report_bug.php', postData)
      .then(response => {
          if (response.data.status === 'success') {
              this.showNotification('Bug reported successfully!', 'success');
              const index = this.requests.findIndex(r => r.request_id === request.request_id);
              if (index !== -1) {
                  this.requests[index].bug_reported = true; // Mark as reported
              }
          } else {
              this.showNotification(response.data.message, 'error');
          }
      })
      .catch(error => {
          this.showNotification('Error reporting bug. Please try again.' + error, 'error');
      });
}
,
looksLikeAddress(input) {
  const ethAddress = /^0x[a-fA-F0-9]{40}$/;
  const btcAddress = /^(bc1([a-zA-HJ-NP-Z0-9]{6,87})|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/;
  const tronAddress = /^T[a-zA-Z0-9]{33}$/;

  return ethAddress.test(input) || btcAddress.test(input) || tronAddress.test(input);
}
,
looksLikeAddressOrWrongInput(input) {
  const trimmed = input.trim();

  // Obvious address patterns
  const ethAddress = /^0x[a-fA-F0-9]{40}$/;
  const btcAddress = /^(bc1([a-zA-HJ-NP-Z0-9]{6,87})|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/;
  const tronAddress = /^T[a-zA-Z0-9]{33}$/;

  // Explorer URL patterns (Etherscan, Tronscan, Blockchain.com, etc.)
  const explorerURL = /(etherscan\.io|tronscan\.org|blockchain\.com|bscscan\.com)\/(tx|address)\/[a-zA-Z0-9]+/;

  // Too short to be valid
  const tooShort = trimmed.length < 40;

  // Contains whitespace or newline
  const hasWhitespace = /\s/.test(trimmed);

  return (
    ethAddress.test(trimmed) ||
    btcAddress.test(trimmed) ||
    tronAddress.test(trimmed) ||
    explorerURL.test(trimmed) ||
    tooShort ||
    hasWhitespace
  );
}
,
    validateTransactionHash(hash) {
      const ethereumPattern = /^0x[a-fA-F0-9]{64}$/;
      const bitcoinPattern = /^[a-fA-F0-9]{64}$/;
      const tronPattern = /^T[a-zA-Z0-9]{33}$/;

      if (ethereumPattern.test(hash)) return 'Ethereum';
      if (bitcoinPattern.test(hash)) return 'Bitcoin';
      if (tronPattern.test(hash)) return 'Tron';
      return null;
    },
  createReport(request) {
  if (!request.result) {
    this.showNotification('No result data available for this request.', 'error');
    return;
  }

  const raw = request.result.trim();
  if (!raw) {
    this.showNotification('Result data was empty.', 'error');
    return;
  }

  // Parse tab-delimited data into an array of rows and columns
  const lines = raw.split('\n');
  if (lines.length <= 1) {
    this.showNotification('No row data found in the result.', 'error');
    return;
  }
  console.log("Creating report");

  const headerLine = lines[0].replace(/\r$/, '');
  const headers = headerLine.split('\t'); // Extract headers from the first line
  const dataLines = lines.slice(1);

  // Helper function to check if a value is valid
  const isValidValue = (value) =>
    value !== undefined &&
    value !== null &&
    value !== '' &&
    value !== 'N/A' &&
    value !== 'undefined';

  const isRowEffectivelyEmpty = (row) => {
    // We expect "row" to already be trimmed cell values
    const firstFourCells = row.slice(0, 4);

    const result = firstFourCells.every((cell) => {
      const v = cell || '';
      const isEmpty =
        v === '' ||
        v === 'N/A' ||
        v === 'undefined';
      return isEmpty;
    });

    return result;
  };

  // Utility to convert an array row -> object keyed by headers
  const toRowData = (cells) => {
    const obj = {};
    headers.forEach((header, i) => {
      obj[header] = cells[i] !== undefined ? cells[i] : '';
    });
    return obj;
  };

  // STEP 1: Group rows into transactions based on Hash column.
  const transactions = [];
  let currentTx = null;

  dataLines.forEach((line) => {
    // Split and trim cells
    const cells = line.split('\t').map((c) => (c || '').trim());

    // Skip fully empty separator rows
    if (isRowEffectivelyEmpty(cells)) {
      return;
    }

    // Skip any repeated header rows that may appear in the data
    if (cells.includes(headers[0])) {
      return;
    }

    const rowData = toRowData(cells);

    const hasHash = isValidValue(rowData['Hash']);

    // If this row has a hash, it starts a new transaction group
    if (hasHash) {
      const tokenSymbolInfo = isValidValue(rowData['TokenSymbol'])
        ? rowData['TokenSymbol']
        : 'N/A';

      const tokenAmountInfo = isValidValue(rowData['Amount'])
        ? `${rowData['Amount']} ${rowData['TokenSymbol']}`
        : 'N/A';

      const fundsFollowedInfo = isValidValue(rowData['Funds followed'])
        ? rowData['Funds followed']
        : 'N/A';

      const balanceInfo =
        isValidValue(rowData['Balance']) && parseFloat(rowData['Balance']) !== 0
          ? `The balance at the time of the transaction was ${rowData['Balance']} ${tokenSymbolInfo}`
          : 'There was no balance at the time of the transaction';

      const fromHashInfo = isValidValue(rowData['From Hash'])
        ? ` From <i>${rowData['From Hash']}</i>`
        : '';

      const fromLabelInfo = isValidValue(rowData['From Label'])
        ? `(From ${rowData['From Label']})`
        : '';

      let notesInfo = isValidValue(rowData['Notes']) ? rowData['Notes'] : '';

      if (notesInfo !== '') {
        notesInfo += ".";
      }

      if (fundsFollowedInfo !== "N/A") {
        notesInfo += ` ${balanceInfo}, and funds followed was ${fundsFollowedInfo}. ${fromHashInfo} ${fromLabelInfo}`;
      } else {
        notesInfo += ` ${balanceInfo}, ${fromHashInfo} ${fromLabelInfo}`;
      }

      // Make the replace actually apply
      notesInfo = notesInfo.replace(". ,", ".");

      currentTx = {
        hash: rowData['Hash'],
        label: rowData['Label'] || '',
        date: rowData['Date'] || '',
        tokenSymbol: tokenSymbolInfo,
        mainAmount: tokenAmountInfo,
        balanceInfo,
        fundsFollowedInfo,
        notesInfo,

        // Primary from/to for quick display
        primaryFrom: '',
        primaryTo: '',

        // Full lists for BTC-style multi-input/multi-output
        inputs: [],  // { address, amount }
        outputs: []  // { address, amount }
      };

      transactions.push(currentTx);
    }

    // If we still do not have a current transaction, we cannot attach this row
    if (!currentTx) {
      return;
    }

    // For every row that belongs to the current transaction,
    // treat FromAddress as inputs and ToAddress as outputs.
    const perRowAmount = isValidValue(rowData['Amount'])
      ? `${rowData['Amount']} ${rowData['TokenSymbol']}`
      : null;

    if (isValidValue(rowData['FromAddress'])) {
      currentTx.inputs.push({
        address: rowData['FromAddress'],
        amount: perRowAmount
      });

      if (!currentTx.primaryFrom) {
        currentTx.primaryFrom = rowData['FromAddress'];
      }
    }

    if (isValidValue(rowData['ToAddress'])) {
      currentTx.outputs.push({
        address: rowData['ToAddress'],
        amount: perRowAmount
      });

      if (!currentTx.primaryTo) {
        currentTx.primaryTo = rowData['ToAddress'];
      }
    }
  });

  if (transactions.length === 0) {
    this.showNotification('Could not find any transactions in the result.', 'error');
    return;
  }

  // STEP 2: Build the HTML report from grouped transactions
  let reportContent = `<h2>Transaction Report</h2>`;

  transactions.forEach((tx, index) => {
    const labelInfo = isValidValue(tx.label)
      ? `<p><strong>Label:</strong> ${tx.label}</p>`
      : '';

    const inputsHtml = tx.inputs.length
      ? `<ul>` +
        tx.inputs
          .map((iObj) => {
            const addr = iObj.address || 'N/A';
            return `<li>${addr}</li>`; // the data does not actually have any input amount only output
          })
          .join('') +
        `</ul>`
      : '<p>No inputs were listed for this transaction.</p>';

    const outputsHtml = tx.outputs.length
      ? `<ul>` +
        tx.outputs
          .map((oObj) => {
            const addr = oObj.address || 'N/A';
            const amt = oObj.amount ? ` - ${oObj.amount}` : '';
            return `<li>${addr}${amt}</li>`;
          })
          .join('') +
        `</ul>`
      : '<p>No outputs were listed for this transaction.</p>';

    // Decide whether to show simple From/To, or full Inputs/Outputs.
    const hasSingleInput = tx.inputs.length === 1;
    const hasSingleOutput = tx.outputs.length === 1;
    const useSimpleFromTo = hasSingleInput && hasSingleOutput;

    let fromToBlock = '';
    let ioBlock = '';

    if (useSimpleFromTo) {
      // Show just From/To, no Inputs/Outputs headings
      const simpleFrom =
        (tx.inputs[0] && tx.inputs[0].address) ||
        tx.primaryFrom ||
        'N/A';

      const simpleTo =
        (tx.outputs[0] && tx.outputs[0].address) ||
        tx.primaryTo ||
        'N/A';

      fromToBlock = `
        <p><strong>From:</strong> ${simpleFrom}</p>
        <p><strong>To:</strong> ${simpleTo}</p>
      `;
      // ioBlock intentionally left empty in this case
    } else {
      // Hide From/To, and instead show full Inputs/Outputs lists
      ioBlock = `
        <h4>Inputs</h4>
        ${inputsHtml}

        <h4>Outputs</h4>
        ${outputsHtml}
      `;
    }

    reportContent += `
      <div class="report-section">
        <h3>Transaction ${index + 1}</h3>
        <p><i><span style="color:grey">${tx.hash}</span></i></p>
        ${labelInfo}
        <p><strong>Date:</strong> ${tx.date || 'N/A'}</p>
        <p><strong>Amount:</strong> ${tx.mainAmount}</p>
        ${fromToBlock}
        ${ioBlock}
        <p><strong>Notes:</strong> ${tx.notesInfo}</p>
      </div>
    `;

    if (tx.notesInfo && tx.notesInfo.includes('Path ends')) {
      reportContent += `<hr style="border: 1px solid #ccc; margin-top: 10px; margin-bottom: 10px;">`;
    }
  });

  // STEP 3: Show the report
  this.showReport(reportContent);
},


showReport(content) {
  // Open a new window and display the report content
  const reportWindow = window.open('', '_blank', 'width=800,height=600');
  reportWindow.document.write(`
    <html>
    <head>
      <title>Transaction Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        .report-section { margin-bottom: 20px; }
        h2, h3 { color: #333; }
      </style>
    </head>
    <body>${content}</body>
    </html>
  `);
  reportWindow.document.close();
},

chooseToAddress(address) {
  this.toAddress = address;
  this.showToAddressPicker = false;
},
submitRequest() {
  if (this.isSubmittingRequest) return; // Block spam
  this.isSubmittingRequest = true;
  this.toAddress = this.toAddress.trim();
this.transactionHash = this.transactionHash.trim();

  this.validationError = '';
if (!this.transactionHash && !this.toAddress) {
  this.validationError = 'You must enter a transaction hash or an address.';
  this.isSubmittingRequest = false;
  return;
}
/*
  // Advanced mode permission check
  if (this.mode === 'normal' && !this.canUseNormalMode) {
    this.showNotification('You do not have permission to use advanced mode. Please use normal mode.', 'error');
       this.isSubmittingRequest = false;
  return;
  }*/
  // Advanced mode permission check
  if (this.mode === 'normal' && !this.canUseNormalMode) {
    this.showNotification("Advanced Mode is not quite ready, please use normal mode for now.  If you'd like to try out Advanced Mode, please contact support.  Advanced mode generates detailed transaction flows, gives hashes, and more.", 'error');
       this.isSubmittingRequest = false;
  return;
  }
  // Validate To Address format (if provided)
  if (this.toAddress && !this.isValidAddress(this.toAddress)) {
    this.showNotification('Invalid to address for detected network type.', 'error');
    this.isSubmittingRequest = false;
    return;
  }
// Access check: Block if not unlimited, not subscribed, and has 0 credits
if (!this.isUnlimitedUser && !this.isSubscriptionUser && this.creditBalance === 0) {
  this.showNotification('You do not have any credits or an active subscription. Please purchase access to continue.', 'error');
  
      this.isSubmittingRequest = false;
  return;
}
  /*
  // Check if user mistakenly entered an address as the hash
  if (this.looksLikeAddress(this.transactionHash)) {
    this.showNotification('This looks like an address, but a transaction hash is required.', 'error');
      this.isSubmittingRequest = false;
   return; // no longer used, now we help the user figure it out
  }*/
 if (this.looksLikeAddress(this.transactionHash)) {
  this.enterAddressAssistanceMode(this.transactionHash);
  return;
}


// Scenario: user entered an address in the To Address field but left txn hash blank
if (!this.transactionHash && this.toAddress && this.looksLikeAddress(this.toAddress)) {
  this.showNotification("Looks like you entered an address instead of a transaction hash. Here's what we found.", 'info');

  this.enterAddressAssistanceMode(this.toAddress);
  this.isSubmittingRequest = false;
  return;
}
  if (this.looksLikeAddressOrWrongInput(this.transactionHash)) {
    this.showNotification('This does not appear to be a valid transaction hash. Please provide a raw hash, not an address or URL.', 'error');
       this.isSubmittingRequest = false;
  return;
  }

  // Check login state
  const username = this.checkLogin();
  if (!this.isLoggedIn) {
    this.showNotification('Please log in to access this tool.', 'error');
      this.isSubmittingRequest = false;
   return;
  }






  const hashType = this.validateTransactionHash(this.transactionHash);
  if (!hashType) {
    this.validationError = 'Invalid transaction hash. Please provide a valid Ethereum, Bitcoin, or Tron hash.';
    this.isSubmittingRequest = false;
     return;
  }
  this.validationError = '';

  /* // removing this check so we can actually validate the To Address
  // If user already provided a valid To Address, we skip the check
  if (this.toAddress && this.isValidAddress(this.toAddress)) {
    this.sendAnalysisRequest(username);
    return;
  }*/

  axios.post('etherscan/check_to_address_needed.php', {
  transaction_hash: this.transactionHash.trim(),
  network: hashType,
  to_address: this.toAddress?.trim().toLowerCase() || null
}).then(response => {

  // Case 1: No To Address provided, but it's required
  if (response.data.requiresToAddress && !this.toAddress) {
    if (Array.isArray(response.data.addressesFound) && response.data.addressesFound.length > 0) {
      this.toAddressSuggestions = response.data.addressesFound;
      this.showToAddressPicker = true;
      this.isSubmittingRequest = false;
      this.showNotification(`This transaction requires a To Address to proceed (${response.data.reason}). Please choose one below. \n\nPicking the wrong address will lead to incorrect results.\n
Use the amount, token, and address to find your own.`, 'error');
    } else {
      this.showNotification(`This transaction requires a To Address to proceed (${response.data.reason}).`, 'error');
     this.isSubmittingRequest = false;
 
    }
    return;
  }

  // Case 2: To Address was provided but it doesn't match
  if (
    this.toAddress &&
    response.data.requiresToAddress &&
    response.data.matchedTo === false &&
    Array.isArray(response.data.addressesFound) &&
    response.data.addressesFound.length > 0
  ) {
    this.toAddressSuggestions = response.data.addressesFound;
    this.showToAddressPicker = true;
      this.isSubmittingRequest = false;
    this.showNotification(`The provided To Address does not match any known destination for this transaction (${response.data.reason}). Please choose a valid one.`, 'error');
    return;
  }
const token = localStorage.getItem('token');
if (!token) {
  this.showNotification('No token found. Please log in again.', 'error');
  this.isSubmittingRequest = false;
  return;
}

axios.post('/etherscan/check_eth_tx_exists.php', {
  txHash: this.transactionHash
}, {
  headers: { Authorization: `Bearer ${token}` }
}).then(res => {
 // console.log('[ETH exists check] response:', res.data);

  //if (res.data?.debug) {
   // console.log('[ETH exists check] Etherscan debug:', res.data.debug);
 // }

  if (res.data?.status === 'ok' && res.data.exists === true) {
    console.log("✅ Valid ETH");
    this.sendAnalysisRequest(username);
    return;
  }

  // ETH failed → Try BTC
  axios.post('/etherscan/check_btc_tx_exists.php', {
    txHash: this.transactionHash
  }, {
    headers: { Authorization: `Bearer ${token}` }
  }).then(res2 => {
    if (res2.data?.status === 'ok' && res2.data.found === true) {
      console.log("✅ Valid BTC");
      this.sendAnalysisRequest(username);
      return;
    }

    // BTC failed → Try TRON
    axios.post('/etherscan/check_tron_tx_exists.php', {
      txHash: this.transactionHash
    }, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res3 => {
      if (res3.data?.status === 'ok' && res3.data.found === true) {
        console.log("✅ Valid TRON");
        this.sendAnalysisRequest(username);
        return;
      }

      // All three failed
      this.isSubmittingRequest = false;
      this.showNotification("Transaction not found on Ethereum, Bitcoin, or Tron. Please double-check the hash, and ensure the transaction isn't on another network (e.g. Base, Polygon, Binance Smart Chain).", 'error');
    }).catch(err3 => {
      console.error("TRON check error:", err3);
      this.isSubmittingRequest = false;
      this.showNotification("Error checking Tron transaction. Please try again.", 'error');
    });

  }).catch(err2 => {
    console.error("BTC check error:", err2);
    this.isSubmittingRequest = false;
    this.showNotification("Error checking Bitcoin transaction. Please try again.", 'error');
  });

}).catch(err => {
  console.error("ETH check error:", err);
  this.isSubmittingRequest = false;
  this.showNotification("Error checking Ethereum transaction. Please try again.", 'error');
});

})
.catch(error => {
      this.isSubmittingRequest = false;
  console.error("To Address check error:", error);
  this.showNotification("Unable to determine if To Address is required. Please try again.", 'error');
});
}
,
isRecognizedToken(token) {
  if (!token) return false;
  return !/^0x[a-fA-F0-9]{40}$/.test(token); // Filter out raw contract addresses posing as symbols
}
,
formatAmount(amount, token) {
  const parsed = parseFloat(amount);
  if (isNaN(parsed)) return amount;

  return `${parsed.toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 8
  })} ${token ? token.toUpperCase?.() : ''}`;
}

,
formatValue(value, network = '') {
  const float = parseFloat(value);
  if (isNaN(float)) return value;

  if (network.toLowerCase() === 'ethereum') {
    const eth = float / 1e18;
    return `${eth.toLocaleString(undefined, {
      minimumFractionDigits: 6,
      maximumFractionDigits: 8
    })} ETH`;
  }

  if (network.toLowerCase() === 'bitcoin') {
    const btc = float / 1e8;
    return `${btc.toLocaleString(undefined, {
      minimumFractionDigits: 8,
      maximumFractionDigits: 8
    })} BTC`;
  }

  return float.toLocaleString(undefined, {
    minimumFractionDigits: 4,
    maximumFractionDigits: 8
  });
}




,

 refreshUserCredits() {
    const token = localStorage.getItem('token');
    if (!token) return;

    axios.get('/etherscan/get_credits.php', {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      if (res.data.status === 'success') {
        this.creditBalance = res.data.balance;
        this.subscriptionExpiresAt = res.data.subscription_expires_at;

        const expires = this.subscriptionExpiresAt ? new Date(this.subscriptionExpiresAt) : null;
        const isExpired = !expires || expires <= new Date();

        this.isSubscriptionUser = !!res.data.is_subscription_user && !isExpired;
        this.isUnlimitedUser = !!res.data.is_unlimited_user;
        this.role = res.data.role;
        this.subscriptionDays = res.data.subscriptionDays || 0;
      } else {
        console.warn("Could not fetch credit/subscription info.");
      }
    })
    .catch(err => {
      console.error("Error fetching credit info:", err);
    });
  },
shorten(addr) {
  return addr ? addr.slice(0, 6) + "..." + addr.slice(-4) : '';
},
handleCopy(fullAddress) {
  navigator.clipboard.writeText(fullAddress).then(() => {
    this.showNotification("Full address copied!", "success");
  });
},

formatDateLocal(ts) {
  const d = new Date(ts * 1000);
  return d.toLocaleString();
},

formatDate(isoString) {
  if (!isoString) return 'N/A';
  return new Date(isoString).toLocaleString();
},

getHopLogicDisplay(v) {
  const x = (v || 'fifo').toLowerCase();
  if (x === 'lifo') return 'LIFO';
  if (x === 'next_out') return 'PIFO';
  return 'FIFO';
},

  clearForm() {
    this.transactionHash = '';
    this.toAddress = '';
 // this.requestType = 'single';
 // this.mode = 'bucket';
 // this.hopLogic = 'fifo';
  },

  /*
  async fetchRequests() {
    const username = this.checkLogin();
    if (!this.isLoggedIn) return;

    const token = localStorage.getItem('token'); // Get JWT from storage
    if (!token) {
        this.showNotification('No token found. Please log in again.', 'error');
        return;
    }

    try {
        const response = await axios.get('etherscan/fetch_requests.php', {
            params: {
                username,
                page: this.currentPage,
                limit: this.limit
            },
            headers: {
                Authorization: `Bearer ${token}`
            }
        });

        if (response.data.status === 'success' && response.data.requests) {
            const formattedRequests = response.data.requests.map(request => ({
                ...request,
                bug_reported: request.bug_reported === 'yes',
                bug_status: request.bug_status
            }));

            formattedRequests.forEach(request => {
                const lastStatus = this.lastStatuses[request.request_id];
                if (lastStatus && lastStatus !== request.status) {
                    this.showNotification(`Request ${request.request_id} status updated to ${request.status}.`, 'info');
                }
                this.lastStatuses[request.request_id] = request.status;
            });

            this.requests = formattedRequests;
            this.totalRequests = response.data.total;

            console.log("requests found");
        } else {
            this.showNotification('Unexpected response structure.', 'error');
        }
    } catch (error) {
    console.error('Error fetching requests:', error);

    // Check if it was a 401 Unauthorized (expired token)
    if (error.response && error.response.status === 401) {
        this.showNotification('Your session has expired. Please log in again.', 'error');

        // Clear the token
        localStorage.removeItem('token');

        // Redirect using Vue Router
        this.$router.push('/login');
        return;
    }

    this.showNotification('Error fetching requests. Please try again.', 'error');
}

}

,
*/
async fetchRequests() {
  const token = localStorage.getItem('token');
  if (!token) {
    this.showNotification('No token found. Please log in again.', 'error');
    this.$router.push('/login');
    return;
  }

  try {
    const response = await axios.get('etherscan/fetch_requests.php', {
      params: {
        page: this.currentPage,
        limit: this.limit
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    if (response.data.status === 'success' && response.data.requests) {
      const formattedRequests = response.data.requests.map(request => ({
        ...request,
        bug_reported: request.bug_reported === 'yes',
        bug_status: request.bug_status
      }));

      formattedRequests.forEach(request => {
        const lastStatus = this.lastStatuses[request.request_id];
        if (lastStatus && lastStatus !== request.status) {
          this.showNotification(`Request ${request.request_id} status updated to ${request.status}.`, 'info');
        }
        this.lastStatuses[request.request_id] = request.status;
      });

      this.requests = formattedRequests;
      this.totalRequests = response.data.total;
    } else {
      this.showNotification('Unexpected response structure.', 'error');
    }
  } catch (error) {
    console.error('Error fetching requests:', error);

    if (error.response && error.response.status === 401) {
      this.showNotification('Your session has expired. Please log in again.', 'error');
      localStorage.removeItem('token');
      this.$router.push('/login');
      return;
    }

    this.showNotification('Error fetching requests. Please try again.', 'error');
  }
}
,
    deleteRequestNow(requestId) {
      const username = this.checkLogin();
      axios.post('etherscan/delete_request.php', { request_id: requestId, username })
        .then(response => {
          if (response.data.status === 'success') {
            this.fetchRequests();
            this.showNotification('Request deleted successfully!', 'success');
          } else {
            this.showNotification(response.data.message, 'error');
          }
        })
        .catch(error => {
          this.showNotification('Error deleting request. Please try again.', 'error');
          console.error('Request deletion error:', error);
        });
    },
  getModeDisplay(mode) {
    return mode === 'bucket' ? 'Normal' : 'Advanced';
  },

    showNotification(message, type) {
      this.notificationMessage = message;
      this.notificationType = type;
    },
    clearNotification() {
      this.notificationMessage = '';
      this.notificationType = '';
    },
       nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchRequests();
      }
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchRequests();
      }
    }
    
  },

  created() {
  const token = localStorage.getItem('token');
  if (!token) {
    this.loading = false;
    return;
  }

    this.prefillFromSession();
  axios.get('/etherscan/get_credits.php', {
    headers: { Authorization: `Bearer ${token}` }
  })
  .then(res => {
    this.loading = false; 
    if (res.data.status === 'success') {
      this.creditBalance = res.data.balance;
    this.subscriptionExpiresAt = res.data.subscription_expires_at;

//  whole day counts as active
const expires = this.subscriptionExpiresAt ? new Date(this.subscriptionExpiresAt) : null;
if (expires) expires.setHours(0, 0, 0, 0);
const today = new Date(); today.setHours(0, 0, 0, 0);
const isExpired = !expires || expires < today;

this.isSubscriptionUser = !!res.data.is_subscription_user && !isExpired;
this.isUnlimitedUser   = !!res.data.is_unlimited_user;
this.role              = res.data.role;

// Days remaining: prefer server if sane, otherwise derive from expires
let derivedDays = 0;
if (this.isSubscriptionUser && this.subscriptionExpiresAt) {
  const diffMs = new Date(this.subscriptionExpiresAt) - new Date();
  derivedDays = diffMs > 0 ? Math.floor(diffMs / (1000 * 60 * 60 * 24)) : 0;
}

const serverDays = (typeof res.data.subscriptionDays === 'number' && res.data.subscriptionDays >= 0)
  ? res.data.subscriptionDays
  : null;

this.subscriptionDays = serverDays ?? derivedDays;

    } else {
      console.warn("Could not fetch credit/subscription info.");
    }
  })
  .catch(err => {
    this.loading = false; // ✅ Also clear loading if it fails
    console.error("Error fetching credit info:", err);
  });
},

  mounted() {
    this.checkLogin(); // Initial login status check
    axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
document.body.classList.add('route-tool');

    this.fetchRequests();// First call, not inside interval

  // Restore "skip credit confirmation" preference (per device)
  const rawSkip = localStorage.getItem('pt_skip_credit_confirm');
  this.skipCreditConfirm = rawSkip === '1';

    this.pollErrorCount = 0;

this.pollingInterval = setInterval(() => {
  this.fetchRequests().catch(() => {
    this.pollErrorCount++;
    if (this.pollErrorCount > 5) {
      clearInterval(this.pollingInterval);
      this.showNotification('Polling stopped due to repeated errors.', 'error');
    }
  });
}, 5000);
    
AuthService.getUserRole().then(data => {
  this.userRole = data.role;
  this.canUseNormalMode = data.canUseNormalMode;
  this.isRestricted = data.isRestricted;
  this.restrictionReason = data.restrictionReason;
  this.restrictionUntil = data.restrictionUntil;
}).catch(error => {
    console.error("Error fetching user role:", error);
    this.showNotification('Error fetching permissions, please reload.', 'error');
  });

    this.boundCloseActionMenus = (e) => this.closeActionMenusOnOutsideClick(e);
  document.addEventListener('click', this.boundCloseActionMenus, true);

  this.boundCloseActionMenusOnKey = (e) => {
    if (e.key === 'Escape') this.closeAllActionMenus();
  };
  document.addEventListener('keydown', this.boundCloseActionMenusOnKey, true);

  },
  beforeUnmount() {
    clearInterval(this.pollingInterval);
    document.body.classList.remove('route-tool');

     if (this.boundCloseActionMenus) {
    document.removeEventListener('click', this.boundCloseActionMenus, true);
    this.boundCloseActionMenus = null;
  }
  if (this.boundCloseActionMenusOnKey) {
    document.removeEventListener('keydown', this.boundCloseActionMenusOnKey, true);
    this.boundCloseActionMenusOnKey = null;
  }
  }
};
</script>
<style scoped>
/* Page tokens */
.tool {
  --bg: #f7f9fc;
  --card: #ffffff;
  --ink: #1f2a37;
  --muted: #6b7280;
  --border: #e5e8ec;
  --accent: #35495e;
  --accent-2: #2c80b4;

  color: var(--ink);
  padding: 1.25rem 1rem 2.5rem;
  background: var(--bg);
}
.container { max-width: 1120px; margin: 0 auto; }

/* Cards */
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(16,24,40,.06);
  padding: 1.2rem;
  margin-top: 1rem;
}
.card__title { margin: 0 0 .45rem; font-size: 1.05rem; }
.card__header { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: .5rem; }

/* Hero */
.card--hero { 
  margin-top: .25rem;
  background:
    radial-gradient(180px 120px at 85% 20%, rgba(44,128,180,.08), transparent 60%),
    radial-gradient(220px 160px at 10% 90%, rgba(53,73,94,.06), transparent 60%),
    linear-gradient(180deg, #ffffff, #f4f7fc);
  border: 1px solid var(--border);
}
.hero {
  display: grid; grid-template-columns: 1fr auto; gap: .6rem; align-items: center;
}
.hero__text h1 { margin: 0; font-size: clamp(1.4rem, 1.2rem + 1vw, 1.9rem); }
.sub { margin: .2rem 0 0; color: var(--muted); }
.login { margin: 0; }
.login.ok { color: #1f6b3a; }
.login.warn { color: #7a3b00; }

/* Access block */
.card--access .access { display: grid; gap: .25rem; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace; }
.muted { color: var(--muted); }
.small { font-size: .92rem; }
.tiny { font-size: .82rem; }
.center { text-align: center; }
.space-top { margin-top: .6rem; }

/* Notice */
.card--notice.success { border-color: #cfe9d8; background: #f2fbf6; }
.card--notice.error { border-color: #f4c7c7; background: #fdeaea; }
.card--notice.info { border-color: #cfe0ec; background: #f2f8fe; }
.notice { display: flex; justify-content: space-between; align-items: center; gap: .5rem; }

/* Grid main */
.grid {
  display: grid;
  grid-template-columns: 1.2fr .8fr;
  gap: 1rem;
  margin-top: 1rem;
}
@media (max-width: 960px) { .grid { grid-template-columns: 1fr; } }

/* Form */
.form { display: grid; gap: .75rem; }
.field { display: grid; gap: .35rem; }
.field.inline { grid-template-columns: 1fr auto; align-items: center; gap: .6rem; }
label { color: var(--muted); font-size: .92rem; }
input, select, textarea {
  width: 100%; min-width: 0; max-width: 100%;
  padding: .7rem .8rem; border-radius: 10px;
  border: 1px solid var(--border); background: #fff; color: var(--ink);
  box-sizing: border-box;
}
input:focus, select:focus, textarea:focus {
  outline: none; box-shadow: 0 0 0 3px rgba(44,128,180,.18); border-color: #c5d9ea;
}
.hint { color: var(--muted); font-size: .9rem; }
.error { color: #8f1d1d; font-weight: 700; }

/* Buttons */
.btn {
  display: inline-flex; align-items: center; justify-content: center;
  padding: .68rem .95rem; border-radius: 10px; font-weight: 700; cursor: pointer;
  border: 1px solid var(--border); background: #fff; color: var(--accent);
  transition: filter 140ms ease, box-shadow 140ms ease;
}
.btn:hover { filter: brightness(.98); }
.btn--primary { border-color: transparent; background: linear-gradient(180deg, var(--accent-2), #1f6d9a); color: #fff; box-shadow: 0 6px 14px rgba(31,109,154,.18); }
.btn--danger { background: #fff5f5; color: #8f1d1d; border-color: #f4c7c7; }
.btn--ghost { background: #fff; color: var(--accent); }
.btn--sm { padding: .45rem .7rem; border-radius: 8px; font-size: .9rem; }
.linkish { background: none; border: none; color: #0b63a9; padding: 0; }
.linkish:hover { text-decoration: underline; }
.actions { display: flex; gap: .6rem; flex-wrap: wrap; }

/* Submitting state */
.submitting { display: grid; place-items: center; gap: .3rem; margin-top: .5rem; }
.spinner {
  width: 18px; height: 18px; border: 3px solid #d9e2eb; border-top-color: #2c80b4;
  border-radius: 50%; animation: spin 1s linear infinite;
}
.spinner.lg { width: 48px; height: 48px; border-width: 6px; }
.spinner.md { width: 24px; height: 24px; border-width: 3px; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Legend */
.flags { list-style: none; padding: 0; margin: .25rem 0 0; display: grid; gap: .4rem; }
.flag { width: 18px; vertical-align: middle; margin-right: 8px; }

/* Modals */
.modal {
  max-width: 720px; width: 100%;
  margin: 1rem auto; background: #fff; border: 1px solid var(--border);
  border-radius: 12px; box-shadow: 0 10px 24px rgba(16,24,40,.14);
  overflow: hidden;
}
.modal__head {
  display: flex; justify-content: space-between; align-items: center;
  padding: .9rem 1rem; background: #f7f9fc; border-bottom: 1px solid var(--border);
}
.modal__body { max-height: 60vh; overflow-y: auto; padding: .75rem 1rem; }
.close { background: none; border: none; font-size: 1.4rem; line-height: 1; cursor: pointer; color: var(--muted); }
.choice { padding: .55rem .4rem; border-bottom: 1px solid #f0f4f8; cursor: pointer; }
.choice:hover { background: #f5faff; }
.tx { display: flex; align-items: center; justify-content: space-between; gap: .75rem; padding: .6rem 0; border-bottom: 1px solid #f0f4f8; }
.tx__summary { line-height: 1.35; }
.short { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace; }

/* Table */
.table-wrap { overflow: auto; border: 1px solid var(--border); border-radius: 12px; }
.table {
  width: 100%; border-collapse: separate; border-spacing: 0; min-width: 880px;
}
.table th, .table td {
  text-align: left; padding: .7rem .8rem; border-bottom: 1px solid var(--border); background: #fff;
}
.table thead th { position: sticky; top: 0; z-index: 1; background: #f9fbfe; color: var(--muted); font-size: .9rem; }
.txcell .truncate { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: bottom; }
.result {
  width: 100%; min-height: 54px; border-radius: 10px; resize: vertical;
  border: 1px solid var(--border); padding: .6rem .7rem;
}
.th-actions, .actions { text-align: right; white-space: nowrap; }

.pill {
  display: inline-block; padding: .2rem .5rem; border-radius: 999px; font-size: .78rem; font-weight: 700;
  border: 1px solid var(--border); background: #f3f6fb; color: #374151;
}
.pill--ok { background: #e9f7ef; color: #1f6b3a; border-color: #cfe9d8; }
.pill--warn { background: #fff6e6; color: #7a3b00; border-color: #f9d7a7; }

/* Pager */
.pager {
  display: flex; gap: .5rem; align-items: center; justify-content: center; margin-top: .6rem; flex-wrap: wrap;
}
.pager--table { justify-content: flex-end; }

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.legend-links {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
}

.linkish { padding: 0 .1rem; }  /* tiny hitbox without looking like a big button */
/* Distinct action styles */
.btn--image {
  background: #eef6ff;
  border-color: #cfe0fb;
  color: #0b63a9;
}
.btn--image:hover { filter: none; background:#e6f1ff; }

.btn--bug {
  background: #fff6f0;
  border-color: #f7d2bd;
  color: #7a3b00;
}
.btn--bug:hover { filter: none; background:#ffeede; }

/* Emphasized primary input */
.field--primary { position: relative; }
.input--primary {
  border-width: 2px;                    /* slightly sturdier */
  padding: .85rem 1rem;
  border-color: #cfe0ec;
  box-shadow: 0 1px 0 rgba(16,24,40,.03);
}
.input--primary:focus {
  border-color: #7fb5da;
  box-shadow: 0 0 0 4px rgba(44,128,180,.16);
}

/* Optional: accent stripe on the left of the field group */
.field--primary::before {
  content: "";
  position: absolute;
  left: -1.2rem; top: .2rem; bottom: .2rem;
  width: 4px;
  border-radius: 4px;
  background: linear-gradient(180deg, #8cc5e6, #2c80b4);
  opacity: .6;
}
@media (max-width: 960px) {
  .field--primary::before { left: -.8rem; }
}
.table .txcell { line-height: 1.3; }
.table .txcell .truncate { max-width: 240px; }

.submit-container {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
}
/* Report modal tweaks */
.modal--report { max-width: 760px; }
.report__meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .4rem .8rem;
  padding: .2rem 0 .6rem;
  border-bottom: 1px solid var(--border);
}
.report__meta .meta__row { display: contents; }
.report__meta .label { color: var(--muted); font-size: .9rem; }
.report__meta .value { font-weight: 600; min-width: 0; }
.report__meta .truncate { display: inline-block; max-width: 100%; overflow: hidden; text-overflow: ellipsis; vertical-align: bottom; }

.report__body { padding: .8rem 0 .4rem; }
.report__text {
  white-space: pre-wrap;
  line-height: 1.5;
  margin: 0;
}

.report__actions {
  display: flex;
  gap: .5rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: .6rem;
}
/* Full‑screen overlay; does not alter body scrolling */
.report-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0,0,0,0.45);
  display: grid;
  place-items: start center;    /* top-ish; change to center if you prefer */
  padding: 24px 16px;
}

/* Your modal content scrolls internally if tall */
.modal--report {
  max-width: 760px;
  width: calc(100% - 32px);
  max-height: min(85vh, 900px);
  overflow: auto;               /* only the modal scrolls, page remains as-is */
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}

.copy-btn {
  position: relative;
  /* Keep width steady so label swap doesn’t jiggle layout */
  min-width: 104px; /* adjust to your font/UI */
}

.copy-btn .copy-label-default,
.copy-btn .copy-label-done {
  display: inline-block;
  transition: opacity 140ms ease;
}

.copy-btn .copy-label-done {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  pointer-events: none;
}

.copy-btn.is-copied .copy-label-default { opacity: 0; }
.copy-btn.is-copied .copy-label-done   { opacity: 1; }

/* Optional: subtle success ring */
.copy-btn.is-copied {
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45);
  animation: copyPulse 600ms ease;
}

@keyframes copyPulse {
  0%   { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); }
  70%  { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
}

/* Disabled state stays readable */
.copy-btn:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}
/* Full-viewport layer that darkens/blur background */
.modal-overlay.modal-overlay--floating {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(0,0,0,0.35);
  backdrop-filter: blur(1px);
  pointer-events: none;   /* lets page still scroll */
  z-index: 9999;
}

/* Dialog itself (your existing spacing rules) */
.modal {
  pointer-events: auto;   /* modal still interactive */
  width: min(560px, 96vw);
  max-height: 90vh;
  overflow: auto;
  background: var(--card-bg, #fff);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
  outline: none;
}


/* Head */
.modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(0,0,0,0.07);
}
.modal__head .close {
  margin-left: 12px;
}

/* Body */
.modal__body {
  padding: 16px 18px;
}
.modal__body p:last-child { margin-bottom: 0; }

/* Actions */
.modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;                 /* space between buttons */
  padding: 14px 18px;
  border-top: 1px solid rgba(0,0,0,0.07);
}

/* Buttons (contrast fix you added) */
.modal .btn.btn--primary {
  background-color: #2d6cdf;
  color: #fff;
  border: none;
}
.modal .btn.btn--primary:hover { background-color: #1e4fa3; }
.modal .btn.btn--ghost { background: transparent; color: #333; }


/* Small niceties */
.report-line { display: flex; gap: 8px; align-items: flex-start; }
.flag { width: 18px; height: 18px; margin-top: 2px; }
.raw-report summary { cursor: pointer; }




.report__disclaimer {
user-select: none;
}
.disclaimer__summary strong { font-weight: 700; }
.disclaimer__version { color: #6b7280; font-size: 12px; }
.disclaimer__hint { margin-left: auto; color: #9ca3af; font-size: 12px; }


/* Card */
.disclaimer__card {
margin-top: 8px;
background: #f8fafc;
border: 1px solid #e5e7eb;
border-radius: 8px;
padding: 12px 12px 8px;
max-height: 180px; /* fits in modal without crowding */
overflow: auto; /* scroll if long */
position: relative;
}


/* subtle top/bottom fade for scrollables */
.disclaimer__card::after {
content: '';
position: sticky;
bottom: -1px;
left: 0; right: 0;
height: 18px;
background: linear-gradient(to bottom, rgba(248,250,252,0), rgba(248,250,252,1));
pointer-events: none;
}


.disclaimer__list {
margin: 8px 0 0 18px;
padding: 0;
line-height: 1.35;
font-size: 13px;
color: #111827;
}
.disclaimer__list li { margin-bottom: 6px; }
.muted { color: #6b7280; font-size: 12px; }


/* Acknowledgement row */
.disclaimer__ack {
display: flex;
align-items: center;
gap: 10px;
margin-top: 10px;
}
.checkbox { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; }


/* Print: always show full disclaimer, no scroll */
@media print {
.disclaimer__card { max-height: none; overflow: visible; background: transparent; border-color: #ddd; }
.disclaimer__summary .disclaimer__hint { display: none; }
}
.advanced-actions { display:flex; gap:.5rem; flex-wrap:wrap; margin:.75rem 0; }

/* Outline button that still looks like a button */
.btn--outline {
  background: transparent;
  border: 1px solid var(--c-border, #d0d7de);
  color: var(--c-text, #111);
  border-radius: 8px;
  padding: .45rem .7rem;
  box-shadow: 0 1px 0 rgba(0,0,0,.04);
}
.btn--outline:hover { background: var(--c-surface-2, #f6f8fa); }

.legend-helper {
  margin-top: 0.75rem;
  padding: 0.75rem 0.9rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.85rem;
}

.legend-helper__body {
  flex: 1;
  min-width: 0;
}

.legend-helper__title {
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.legend-helper__text {
  margin: 0;
  opacity: 0.85;
}

.legend-helper__cta {
  white-space: nowrap;
  font-size: 0.8rem;
  padding: 0.35rem 0.7rem;
  align-self: center;
}

/* Optional: if .space-top is global already, this will just reuse it.
   If not, this keeps spacing consistent. */
.space-top2 {
  margin-top: 0.75rem;
}

.btn--external {
  background: #fff;
  border-color: #b9d9c6;
  color: #146b3a;
}
.btn--external:hover {
  filter: none;
  background: #f4fff8;
}
.actions { display: flex; gap: .6rem; flex-wrap: wrap; }
.actions .btn { margin-left: .35rem; }


/* Dropdown menu */
.action-menu {
  position: relative;
}

.action-menu > summary {
  list-style: none;
}

.action-menu > summary::-webkit-details-marker {
  display: none;
}

.action-menu__panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 180px;
  padding: 10px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid var(--border);
  box-shadow: 0 10px 26px rgba(0,0,0,.12);
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.menu-item {
  width: 100%;
  justify-content: flex-start;
}

/* New button style in-table */
.btn--invest {
  border-color: #bfe6cf;
  background: #f2fff6;
  color: #146b3a;
}
.btn--invest:hover {
  filter: none;
  background: #e9fff0;
}
.report__callout {
  margin: 12px 0;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid var(--border);
  background: #fbfffd;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
}

.report__callout .callout__text {
  min-width: 0;
}

.report__callout strong {
  display: block;
}


/* Remove old margin spacing if it still exists somewhere */
.table td.actions .btn {
  margin-left: 0 !important;
}

/* Allow the long Investigation button to shrink instead of forcing wrapping */
.table td.actions .btn--invest {
  min-width: 0;           /* Key for flex containers: allows shrink */
  max-width: 170px;       /* Tune this: 150-220 usually feels right */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.action-menu > summary::after {
  content: "▾";
  margin-left: 8px;
  font-size: .85em;
  opacity: .7;
}
/* Gold CTA button (reusable) */
.btn--cta {
  border-color: #d6a10a;
  background: linear-gradient(180deg, #ffe07a, #f3b61a);
  color: #111827;
  box-shadow: 0 10px 18px rgba(243, 182, 26, 0.22);
  text-shadow: 0 1px 0 rgba(255,255,255,0.45);
}

.btn--cta:hover {
  filter: none;
  background: linear-gradient(180deg, #ffe89a, #f6c233);
  box-shadow: 0 12px 22px rgba(243, 182, 26, 0.26);
}

.btn--cta:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 3px rgba(243, 182, 26, 0.35),
    0 10px 18px rgba(243, 182, 26, 0.22);
}

/* Optional: keep a slightly “buttonier” pressed feel */
.btn--cta:active {
  transform: translateY(1px);
}
/* In table action cells, slightly reduce shadow so it doesn’t feel too heavy */
.table td.actions .btn--cta {
  box-shadow: 0 8px 14px rgba(243, 182, 26, 0.20);
}

/* If you use truncation on the investigation button */
.table td.actions .btn--cta {
  min-width: 0;
  max-width: 170px;    /* keep your existing cap */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.inline-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.inline-row .field.inline {
  margin: 0;
  flex: 1 1 0;
}

.help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 6px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,0.25);
  font-size: 11px;
  line-height: 16px;
  cursor: help;
  user-select: none;
}
/* One clean row: label on the left, controls on the right */
.modes-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* "Modes:" label - keep it stable and aligned */
.modes-row__label {
  font-weight: 600;
  white-space: nowrap;
  padding-top: 2px; /* tiny nudge to align with selects */
}

/* Controls cluster to the right */
.modes-row__controls {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap; /* allows nice wrap on narrow screens */
}

/* Each control is a compact inline group */
.modes-row__control {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Keep selects from getting too small; adjust to taste */
.modes-row__control select {
  min-width: 190px;
}

/* Your existing help style works, but keep it aligned beside select */
.help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid rgba(0,0,0,0.25);
  font-size: 11px;
  line-height: 16px;
  cursor: help;
  user-select: none;
}

/* Screen-reader-only utility */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Optional: on very narrow widths, stack label over controls cleanly */
@media (max-width: 520px) {
  .modes-row {
    flex-direction: column;
    align-items: flex-start;
  }
  .modes-row__controls {
    width: 100%;
    justify-content: flex-start;
  }
}
.modes-row__control select {
  width: auto;
  max-width: 260px;
}
/* Ensure tooltips aren't clipped by an ancestor with overflow:hidden */
.modes-row,
.modes-row__controls,
.modes-row__control {
  overflow: visible;
}

.tip {
  position: relative;
}

/* Tooltip bubble */
.tip::after {
  content: attr(data-tip);
  position: absolute;
  left: 50%;
  top: calc(100% + 10px);
  transform: translateX(-50%);
  min-width: 260px;
  max-width: 360px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.18);
  background: #fff;
  color: #111;
  font-size: 12px;
  line-height: 1.35;
  white-space: pre-line; /* respects \n */
  box-shadow: 0 12px 24px rgba(0,0,0,0.12);
  z-index: 50;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

/* Small arrow */
.tip::before {
  content: "";
  position: absolute;
  left: 50%;
  top: calc(100% + 4px);
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent transparent rgba(0,0,0,0.12) transparent;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}

/* Show on hover + keyboard focus */
.tip:hover::after,
.tip:focus::after,
.tip:hover::before,
.tip:focus::before {
  opacity: 1;
  visibility: visible;
}
/* Keep the header aligned */
.th-actions,
.td-actions {
  text-align: right;
  white-space: nowrap;
}

/* IMPORTANT: td stays a table-cell (default); do not set display:flex on td */
.table td.td-actions {
  vertical-align: middle;
}

/* Flex lives inside the cell */
.actions-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: .45rem;
  flex-wrap: nowrap;
  white-space: nowrap;
}

/* No extra left margins inside the actions row */
.actions-row .btn {
  margin-left: 0 !important;
}

/* If you still want investigation truncation */
.actions-row .btn--cta {
  min-width: 0;
  max-width: 170px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

</style>
<style>
.route-tool #app > nav { z-index: 1000; }
.route-tool,
.route-tool #app,
.route-tool #app > main { background: #f7f9fc; }
</style>
